(()=>{

  /*------------------------------------*\
    #UTITLITIES
  \*------------------------------------*/

  function throttle(callback) {
    let requestId = null, lastArgs;

    const later = (context) => () => {
      requestId = null;
      callback.apply(context, lastArgs);
    };

    const throttled = (...args) => {
      lastArgs = args;
      if (requestId === null) {
        requestId = requestAnimationFrame(later(this));
      }
    };

    throttled.cancel = () => {
      cancelAnimationFrame(requestId);
      requestId = null;
    };

    return throttled;
  }


  function debounce(fn, delay) {
    let timer = null;

    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fn.apply(this, args);
      }, delay);
    };
  }

  // This function listens for a specified event on an element and resolves the promise when the event is fired. This pattern is useful for asynchronously managing state changes that depend on certain events.
  function waitForEvent(element, eventName) {
    return new Promise((resolve) => {
      const done = (event) => {
        if (event.target === element) {
          element.removeEventListener(eventName, done);
          resolve(event);
        }
      };

      element.addEventListener(eventName, done);
    });
  }

  /**
   * An enhancement of `fetch` allowing us to cache responses so we can avoid making further network requests.
   */
  var cachedMap = new Map();

  function cachedFetch(url, options) {
    // cachedMap stores key-value pairs for successful fetch requests. The key is the url 
    const cacheKey = url;

    if (cachedMap.has(cacheKey)) {
      /**
       * If we have a cached response, we return a promise that resolves to a Response object.
       * This response is created using a Blob with the cached content, allowing us to use cached content as if it was a fresh response from the server.
       */
      return Promise.resolve(new Response(new Blob([cachedMap.get(cacheKey)])));
    }

    // If there is no cached response, we run a fetch request
    return fetch(url, options).then((response) => {
      if (response.status === 200) {
        // If successful, get the contentType
        const contentType = response.headers.get("Content-Type");

        // Check if the contentType is json or text
        if (contentType && (contentType.match(/application\/json/i) || contentType.match(/text\//i))) {
          // As Response object can only be consumed once, we need to clone the response to extract the content
          // We store the cloned response as text, which then gets cached
          response.clone().text().then((content) => {
            cachedMap.set(cacheKey, content);
          });
        }
      }
      return response;
    });
  }


  class Player extends EventTarget {
    constructor(durationInSec, stopOnVisibility = true) {
      super();
  
      this.callback = () => this.dispatchEvent(new CustomEvent("player:end"));
      this.duration = durationInSec * 1000;
      this.remainingTime = this.duration;
      this.startTime = null;
      this.timer = null;
      this.state = "paused";
  
      this.onVisibilityChangeListener = this.onVisibilityChange.bind(this);
  
      if (stopOnVisibility) {
        document.addEventListener("visibilitychange", this.onVisibilityChangeListener);
      }
    }
  
    getDuration() {
      return this.duration / 1000;
    }
  
    setDuration(durationInSec) {
      this.duration = durationInSec * 1000;
      this.remainingTime = this.duration;
    }
  
    pause() {
      if (this.state !== "started") {
        return;
      }
      clearTimeout(this.timer);
      this.state = "paused";
      this.remainingTime -= new Date().getTime() - this.startTime;
      this.dispatchEvent(new CustomEvent("player:pause", { detail: { duration: this.duration / 1000, remainingTime: this.remainingTime / 1000 } }));
    }
  
    resume(restartTimer = false) {
      if (this.state !== "stopped") {
        if (restartTimer) {
          this.start();
        } else {
          clearTimeout(this.timer);
          this.startTime = new Date().getTime();
          this.state = "started";
          this.timer = setTimeout(this.callback, this.remainingTime);
          this.dispatchEvent(new CustomEvent("player:resume", { detail: { duration: this.duration / 1000, remainingTime: this.remainingTime / 1000 } }));
        }
      }
    }
  
    start() {
      clearTimeout(this.timer);
      this.startTime = new Date().getTime();
      this.state = "started";
      this.remainingTime = this.duration;
      this.timer = setTimeout(this.callback, this.remainingTime);
      this.dispatchEvent(new CustomEvent("player:start", { detail: { duration: this.duration / 1000, remainingTime: this.remainingTime / 1000 } }));
    }
  
    stop() {
      clearTimeout(this.timer);
      this.state = "stopped";
      this.dispatchEvent(new CustomEvent("player:stop"));
    }
  
    onVisibilityChange() {
      if (document.visibilityState === "hidden") {
        this.pause();
      } else if (document.visibilityState === "visible") {
        this.resume();
      }
    }
  }
  
  if (!window.customElements.get("player-element")) {
    window.customElements.define("player-element", Player);
  }
  





  /*------------------------------------*\
    #SHOPIFY UTITLITIES
  \*------------------------------------*/

  function extractSectionId(element) {
    element = element.classList.contains("shopify-section") ? element : element.closest(".shopify-section");
    return element.id.replace("shopify-section-", "");
  }


  function formatMoney(cents, format = "") {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;

    function defaultTo(value2, defaultValue) {
      return value2 == null || value2 !== value2 ? defaultValue : value2;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100).toFixed(precision);
      let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";

      return dollarsAmount + centsAmount;
    }

    let value = "";

    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
        break;
    }

    if (formatString.indexOf("with_comma_separator") !== -1) {
      return formatString.replace(placeholderRegex, value);
    } else {
      return formatString.replace(placeholderRegex, value);
    }
  }

  var loadedProducts = {};

  var ProductLoader = class {
    static load(productHandle) {
      if (!productHandle) {
        return;
      }

      if (loadedProducts[productHandle]) {
        return loadedProducts[productHandle];
      }

      loadedProducts[productHandle] = new Promise(async (resolve, reject) => {
        const response = await fetch(`${Shopify.routes.root}products/${productHandle}.js`);

        if (response.ok) {
          const responseAsJson = await response.json();
          resolve(responseAsJson);
        } else {
          reject(`
            Attempted to load information for product with handle ${productHandle}, but this product is in "draft" mode. You won't be able to
            switch between variants or access to per-variant information. To fully preview this product, change temporarily its status
            to "active".
          `);
        }
      });

      return loadedProducts[productHandle];
    }
  }

  var CountrySelector = class extends HTMLElement {
    constructor() {
      super();
      this._onCountryChangedListener = this._onCountryChanged.bind(this);
    }

    connectedCallback() {
      this.countryElement = this.querySelector('[name="address[country]"]');
      this.provinceElement = this.querySelector('[name="address[province]"]');
      this.countryElement.addEventListener("change", this._onCountryChangedListener);

      /**
       * If the country selector is in the Shipping Estimator, we define this variable.
       */
      this.inShippingEstimator = this.closest("shipping-estimator") ? true : void 0;

      if (this.hasAttribute("country") && this.getAttribute("country") !== "") {
        this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute("country")));
        this.countryElement.dispatchEvent(new Event("change"));
      } else {
        this._onCountryChanged();
      }
    }

    disconnectedCallback() {
      this.countryElement.removeEventListener("change", this._onCountryChangedListener);
    }

    _onCountryChanged() {
      const option = this.countryElement.options[this.countryElement.selectedIndex]; const provinces = JSON.parse(option.getAttribute("data-provinces"));

      this.provinceElement.parentElement.hidden = provinces.length === 0;

      if (provinces.length === 0) {
        return;
      }

      this.provinceElement.innerHTML = "";
      provinces.forEach((data) => {
        const selected = data[1] === this.getAttribute("province");
        this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
      });

      // If UK is selected, make England the default province
      if (this.inShippingEstimator && this.countryElement.value === 'United Kingdom') {
        this.provinceElement.value = 'England';
      }
    }
  };

  if (!window.customElements.get("country-selector")) {
    window.customElements.define("country-selector", CountrySelector);
  }




  /*------------------------------------*\
    #IMAGE UTILITIES
  \*------------------------------------*/

  function imageLoaded(imageOrArray) {
    if (!imageOrArray) {
      return Promise.resolve();
    }

    imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);

    return Promise.all(imageOrArray.map((image) => {
      return new Promise((resolve) => {
        if (image.tagName === "IMG" && image.complete || !image.offsetParent) {
          resolve();
        } else {
          image.onload = () => resolve();
        }
      });
    }));
  }

  function generateSrcset(imageObject, widths = []) {
    const imageUrl = new URL(imageObject["src"]);

    return widths.filter((width) => width <= imageObject["width"]).map((width) => {
      imageUrl.searchParams.set("width", width.toString());
      return `${imageUrl.href} ${width}w`;
    }).join(", ");
  }

  function createMediaImg(media, widths = [], properties = {}) {
    const image = new Image(media["preview_image"]["width"], media["preview_image"]["height"]), featuredMediaUrl = new URL(media["preview_image"]["src"]);

    for (const propertyKey in properties) {
      image.setAttribute(propertyKey, properties[propertyKey]);
    }

    image.alt = media["alt"];
    image.src = featuredMediaUrl.href;
    image.srcset = generateSrcset(media["preview_image"], widths);
    return image;
  }



  /*------------------------------------*\
    #ASSORTED UTILITY ELEMENTS
  \*------------------------------------*/

  /**
   * Gets the height of an element so we can store and update a CSS variable.
   */
  var HeightObserver = class extends HTMLElement {
    constructor() {
      super();
      if (window.ResizeObserver) {
        new ResizeObserver(this._updateCustomProperties.bind(this)).observe(this);
      }
    }

    connectedCallback() {
      if (!window.ResizeObserver) {
        document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${this.clientHeight.toFixed(1)}px`);
      }
    }

    _updateCustomProperties(entries) {
      requestAnimationFrame(() => {
        entries.forEach((entry) => {
          if (entry.target === this) {
            const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
            document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${Math.round(height)}px`);
          }
        });
      });
    }
  };

  if (!window.customElements.get("height-observer")) {
    window.customElements.define("height-observer", HeightObserver);
  }

  /**
   * This allows us to scroll a sticky container if the content is taller than the viewport.
   */
  var SafeSticky = class extends HTMLElement {
    constructor() {
      super();
      this._resizeObserver = new ResizeObserver(this._recalculateStyles.bind(this));
      this._checkPositionListener = throttle(this._checkPosition.bind(this));
      this._initialTop = 0;
      this._lastKnownY = 0;
      this._currentTop = 0;
      this._position = "relative";
    }

    connectedCallback() {
      Motion.inView(this, () => {
        this._resizeObserver.observe(this);
        window.addEventListener("scroll", this._checkPositionListener);

        return () => {
          this._resizeObserver.unobserve(this);
          window.removeEventListener("scroll", this._checkPositionListener);
        };
      }, { margin: "500px" });
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this._checkPositionListener);
    }

    _recalculateStyles() {
      this.style.removeProperty("top");
      const computedStyles = getComputedStyle(this);
      this._initialTop = parseInt(computedStyles.top);
      this._position = computedStyles.position;
      this._checkPosition.call(this);
    }

    _checkPosition() {
      if (this._position !== "sticky") {
        return this.style.removeProperty("top");
      }

      let bounds = this.getBoundingClientRect(); 
      let maxTop = bounds.top + window.scrollY - this.offsetTop + this._initialTop; 
      let minTop = this.clientHeight - window.innerHeight + 20;

      if (window.scrollY < this._lastKnownY) {
        this._currentTop = this._currentTop - window.scrollY - this._lastKnownY;
      } else {
        this._currentTop = this._currentTop + this._lastKnownY - window.scrollY;
      }

      this._currentTop = Math.min(Math.max(this._currentTop, -minTop), maxTop, this._initialTop);
      this._lastKnownY = window.scrollY;
      this.style.top = `${Math.round(this._currentTop)}px`;
    }
  };

  if (!window.customElements.get("safe-sticky")) {
    window.customElements.define("safe-sticky", SafeSticky);
  }



  /*------------------------------------*\
    #NAVIGATION ELEMENTS
  \*------------------------------------*/

  var PageDots = class extends HTMLElement {
    _pendingAnimationControls = [];

    connectedCallback() {
      this._abortController = new AbortController();
      this.items = Array.from(this.children);

      // Add event listener to all buttons
      this.items.forEach((button, index) => button.addEventListener("click", () => this.select(index), { signal: this._abortController.signal }));

      // If we filter our controlled elements e.g. on a product gallery carousel we only show certain images upon variant selection, this will filter our dots or thumbnails
      this.addEventListener("control:filter", this._filterItems, { signal: this._abortController.signal });

      // Link the page dots to changes on the controlled element
      if (this.controlledElement) {
        this.controlledElement.addEventListener("carousel:change", (event) => this.select(event.detail.index, false), { signal: this._abortController.signal });
      }

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target)));
      }

      if (this.controlledElement.hasAttribute("autoplay")) {
        this.duration = this.controlledElement.getAttribute("autoplay");
        this.circles = Array.from(this.querySelectorAll(".numbered-dots__item"));

        const activeElement = this.querySelector('[aria-current="true"]');
        const activeCircle = activeElement.querySelector("circle:last-child");

        if (this.controlledElement.parentElement.classList.contains("hidden")) {
          return;
        }

        Motion.animate(activeCircle, { strokeDasharray: [`0px, ${activeCircle.getTotalLength()}px`, `${activeCircle.getTotalLength()}px, ${activeCircle.getTotalLength()}px`] }, { duration: this.duration, easing: "linear" });
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get autoplayoff() {
      return this.hasAttribute("autoplay-off");
    }

    get hasNumberedDots() {
      return this.hasAttribute("numbered-dots");
    }

    get controlledElement() {
      return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
    }

    get selectedIndex() {
      return this.items.findIndex((button) => button.getAttribute("aria-current") === "true");
    }

    select(selectedIndex, dispatchEvent = true) {
      if (this.hasAttribute("align-selected")) {
        const scrollElement = this.getAttribute("align-selected") !== "" ? this.closest(this.getAttribute("align-selected")) : this;

        /**
         * If we have scrolling tabs, we want to scroll to the selected tab and position it
         * in the center if possible. This guarantees the user will see the previous and next
         * tabs.
         */

        scrollElement.scrollTo({
          left: this.items[selectedIndex].offsetLeft - scrollElement.clientWidth / 2 + this.items[selectedIndex].clientWidth / 2,
          top: this.items[selectedIndex].offsetTop - scrollElement.clientHeight / 2 - this.items[selectedIndex].clientHeight / 2,
          behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto"
        });
      }

      if (this.selectedIndex === selectedIndex) {
        return;
      }

      // Set aria-current attribute on all elements. Selected element gets true while others get false.
      this.items.forEach((button, index) => button.setAttribute("aria-current", selectedIndex === index ? "true" : "false"));

      if (dispatchEvent) {
        this._dispatchEvent(selectedIndex);
      }

      if (this.controlledElement.parentElement.classList.contains("hidden")) {
        return;
      }

      if (this.circles) {
        this.circles.forEach((item) => {
          if (window.getComputedStyle(item).display === "none") {
            return;
          }

          const circle = item.querySelector("circle:last-child");

          let circleAnimationControl = null;

          if (item.parentElement.parentElement.getAttribute("aria-current") === "true") {
            circleAnimationControl = Motion.animate(circle, { strokeDasharray: [`0px, ${circle.getTotalLength()}px`, `${circle.getTotalLength()}px, ${circle.getTotalLength()}px`] }, { duration: this.duration, easing: "linear" });
          } else {
            circleAnimationControl = Motion.animate(circle, { strokeDasharray: `${circle.getTotalLength()}px, ${circle.getTotalLength()}px` }, { duration: 0, easing: "linear" });
          }
        });
      }
    }

    _filterItems(event) {
      this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
    }

    _dispatchEvent(index) {
      (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:select", { bubbles: true, cancelable: true, detail: { index: index, externalClick: true } }));
    }
  };

  var PrevButton = class extends HTMLButtonElement {
    connectedCallback() {
      this._abortController = new AbortController();

      this.addEventListener("click", () => (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:prev", { bubbles: true, cancelable: true })), { signal: this._abortController.signal });

      // Enable or disable the prev button when approaching end of a scrolling element
      if (this.controlledElement) {
        this.controlledElement.addEventListener("scroll:edge-nearing", (event) => this.disabled = event.detail.position === "start", { signal: this._abortController.signal });

        this.controlledElement.addEventListener("scroll:edge-leaving", (event) => this.disabled = event.detail.position === "start" ? false : this.disabled, { signal: this._abortController.signal });

        // Disables the previous button on the first slide for non-looping effectCarousels 
        if (!this.controlledElement.loop) {  
          this.controlledElement.addEventListener("effect-carousel:change", (event) => {
            this.disabled = event.detail.index == 0;
          });
        }
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get controlledElement() {
      return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
    }
  };

  var NextButton = class extends HTMLButtonElement {
    connectedCallback() {
      this._abortController = new AbortController();

      this.addEventListener("click", () => (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:next", { bubbles: true, cancelable: true })), { signal: this._abortController.signal });

      if (this.controlledElement) {
        this.controlledElement.addEventListener("scroll:edge-nearing", (event) => this.disabled = event.detail.position === "end", { signal: this._abortController.signal });

        this.controlledElement.addEventListener("scroll:edge-leaving", (event) => this.disabled = event.detail.position === "end" ? false : this.disabled, { signal: this._abortController.signal });

        // Disables the next button on the last slide for non-looping effectCarousels 
        if (!this.controlledElement.loop) {  
          this.controlledElement.addEventListener("effect-carousel:change", (event) => {
            this.disabled = event.detail.index == this.controlledElement.items.length - 1;
          });
        }
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }
    
    get controlledElement() {
      return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
    }
  };

  if (!window.customElements.get("page-dots")) {
    window.customElements.define("page-dots", PageDots);
  }

  if (!window.customElements.get("prev-button")) {
    window.customElements.define("prev-button", PrevButton, { extends: "button" });
  }

  if (!window.customElements.get("next-button")) {
    window.customElements.define("next-button", NextButton, { extends: "button" });
  }

  /**
   * Adds gesture event listeners. For example, we can use this in our `EffectCarousel` which allows us to check for a swipe and then change slides.
   */
  var GestureArea = class {
    constructor(domElement, { thresholdDistance = 80, thresholdTime = 500, signal = null } = {}) {
      this._domElement = domElement;
      this._thresholdDistance = thresholdDistance;
      this._thresholdTime = thresholdTime;
      this._signal = signal;
      this._firstClientX = null;
      this._tracking = false;
      this._start = {};
  
      this._domElement.addEventListener("touchstart", this._touchStart.bind(this), { passive: true, signal: this._signal });
      this._domElement.addEventListener("touchmove", this._preventTouch.bind(this), { passive: false, signal: this._signal });
      this._domElement.addEventListener("pointerdown", this._gestureStart.bind(this), { signal: this._signal });
      this._domElement.addEventListener("pointermove", this._gestureMove.bind(this), { passive: false, signal: this._signal });
      this._domElement.addEventListener("pointerup", this._gestureEnd.bind(this), { signal: this._signal });
      this._domElement.addEventListener("pointerleave", this._gestureEnd.bind(this), { signal: this._signal });
      this._domElement.addEventListener("pointercancel", this._gestureEnd.bind(this), { signal: this._signal });
    }
  
    _touchStart(event) {
      this._firstClientX = event.touches[0].clientX;
    }
  
    _preventTouch(event) {
      if (Math.abs(event.touches[0].clientX - this._firstClientX) > 10) {
        event.preventDefault();
      }
    }
  
    _gestureStart(event) {
      this._tracking = true;
      this._start = {
        time: new Date().getTime(),
        x: event.clientX,
        y: event.clientY
      };
    }
  
    _gestureMove(event) {
      if (this._tracking) {
        event.preventDefault();
      }
    }
  
    _gestureEnd(event) {
      if (!this._tracking) {
        return;
      }
      this._tracking = false;
      const now = new Date().getTime();
      const deltaTime = now - this._start.time;
      const deltaX = event.clientX - this._start.x;
      const deltaY = event.clientY - this._start.y;
  
      if (deltaTime > this._thresholdTime) {
        return;
      }
  
      let matchedEvent;
      if (deltaX === 0 && deltaY === 0 && !event.target.matches("a, button, a :scope, button :scope")) {
        matchedEvent = "tap";
      } else if (deltaX > this._thresholdDistance && Math.abs(deltaY) < this._thresholdDistance) {
        matchedEvent = "swiperight";
      } else if (-deltaX > this._thresholdDistance && Math.abs(deltaY) < this._thresholdDistance) {
        matchedEvent = "swipeleft";
      } else if (deltaY > this._thresholdDistance && Math.abs(deltaX) < this._thresholdDistance) {
        matchedEvent = "swipedown";
      } else if (-deltaY > this._thresholdDistance && Math.abs(deltaX) < this._thresholdDistance) {
        matchedEvent = "swipeup";
      }
  
      if (matchedEvent) {
        this._domElement.dispatchEvent(new CustomEvent(matchedEvent, { bubbles: true, composed: true, detail: { originalEvent: event } }));
      }
    }
  }

  /**
   * Adds scroll event listeners. We use this on many of our scroll carousels to determine the scroll position and enable or disable carousel navigation as needed. E.g. if we reach the end of a scroll container, any 'next' buttons should be disabled.
   */
  var ScrollArea = class {
    constructor(element, abortController2 = null) {
      this._element = element;
      this._allowTriggerNearingStartEvent = false;
      this._allowTriggerLeavingStartEvent = true;
      this._allowTriggerNearingEndEvent = true;
      this._allowTriggerLeavingEndEvent = false;

      new ResizeObserver(this._checkIfScrollable.bind(this)).observe(element);
      this._element.addEventListener("scroll", throttle(this._onScroll.bind(this)), { signal: abortController2?.signal });
    }

    get scrollNearingThreshold() {
      // return 125;
      return 48;
    }

    get scrollDirection() {
      if (this._element.scrollWidth > this._element.clientWidth) {
        return "inline";
      } else if (this._element.scrollHeight - this._element.clientHeight > 1) {
        return "block";
      } else {
        return "none";
      }
    }

    _checkIfScrollable() {
      this._element.classList.toggle("is-scrollable", this.scrollDirection !== "none");
    }

    _onScroll() {
      clearTimeout(this._scrollTimeout);
      this._lastScrollPosition = this._lastScrollPosition ?? (this.scrollDirection === "inline" ? Math.abs(this._element.scrollLeft) : Math.abs(this._element.scrollTop));

      let direction;

      if (this.scrollDirection === "inline") {
        direction = this._lastScrollPosition > Math.abs(this._element.scrollLeft) ? "start" : "end";
        this._lastScrollPosition = Math.abs(this._element.scrollLeft);
      } else {
        direction = this._lastScrollPosition > Math.abs(this._element.scrollTop) ? "start" : "end";
        this._lastScrollPosition = Math.abs(this._element.scrollTop);
      }

      const scrollPosition = Math.round(Math.abs(this.scrollDirection === "inline" ? this._element.scrollLeft : this._element.scrollTop));

      const scrollMinusSize = Math.round(this.scrollDirection === "inline" ? this._element.scrollWidth - this._element.clientWidth : this._element.scrollHeight - this._element.clientHeight);

      if (direction === "start" && this._allowTriggerNearingStartEvent && scrollPosition <= this.scrollNearingThreshold) {
        this._allowTriggerNearingStartEvent = false;
        this._allowTriggerLeavingStartEvent = true;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "start" } }));
      } else if (direction === "end" && scrollPosition > this.scrollNearingThreshold) {
        this._allowTriggerNearingStartEvent = true;
        if (this._allowTriggerLeavingStartEvent) {
          this._allowTriggerLeavingStartEvent = false;
          this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "start" } }));
        }
      }

      if (direction === "end" && this._allowTriggerNearingEndEvent && scrollMinusSize <= scrollPosition + this.scrollNearingThreshold) {
        this._allowTriggerNearingEndEvent = false;
        this._allowTriggerLeavingEndEvent = true;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "end" } }));
      } else if (direction === "start" && scrollMinusSize > scrollPosition + this.scrollNearingThreshold) {
        this._allowTriggerNearingEndEvent = true;
        if (this._allowTriggerLeavingEndEvent) {
          this._allowTriggerLeavingEndEvent = false;
          this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "end" } }));
        }
      }

      if (window.onscrollend === void 0) {
        this._scrollTimeout = setTimeout(() => {
          this._element.dispatchEvent(new CustomEvent("scrollend", { bubbles: true, composed: true }));
        }, 75);
      }
    }
  };

  /**
   * Used for many of our scroll containers to provide a visual element tracking scroll progress.
   */
  var ScrollProgress = class extends HTMLElement {
    connectedCallback() {
      this.scrolledElement.addEventListener("scroll", throttle(this._updateScrollProgress.bind(this)));

      if (window.ResizeObserver) {
        new ResizeObserver(this._updateScrollProgress.bind(this)).observe(this.scrolledElement);
      }
    }

    get scrolledElement() {
      return this._scrolledElement = this._scrolledElement || document.getElementById(this.getAttribute("observes"));
    }

    _updateScrollProgress() {
      const scrollLeft = document.dir === "ltr" ? this.scrolledElement.scrollLeft : Math.abs(this.scrolledElement.scrollLeft); 
      const advancement = (scrollLeft + this.scrolledElement.clientWidth) / this.scrolledElement.scrollWidth;
      this.style.setProperty("--scroll-progress", Math.max(0, Math.min(advancement, 1)).toFixed(6));
    }
  };

  if (!window.customElements.get("scroll-progress")) {
    window.customElements.define("scroll-progress", ScrollProgress);
  }

  /**
   * #SCROLL SHADOW
   * The following elements allow us to add a Scroll Shadow parent to scroll carousels. This tracks the position of a scroll container and adds a shadow as a visual indicator that there is more content to scroll.
   */
  var template = `
    <style>
      :host {
        display: inline-block;
        contain: layout;
        position: relative;
      }
      
      :host([hidden]) {
        display: none;
      }
      
      s {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        pointer-events: none;
        background-image:
          var(--scroll-shadow-top, linear-gradient(to bottom, rgb(var(--background)), rgb(var(--background) / 0))),
          var(--scroll-shadow-bottom, linear-gradient(to top, rgb(var(--background)), rgb(var(--background) / 0))),
          var(--scroll-shadow-left, linear-gradient(to right, rgb(var(--background)), rgb(var(--background) / 0))),
          var(--scroll-shadow-right, linear-gradient(to left, rgb(var(--background)), rgb(var(--background) / 0)));
        background-position: top, bottom, left, right;
        background-repeat: no-repeat;
        background-size: 100% var(--top, 0), 100% var(--bottom, 0), var(--left, 0) 100%, var(--right, 0) 100%;
      }
    </style>
    <slot></slot>
    <s></s>
  `;

  var Updater = class {
    constructor(targetElement) {
      this.scheduleUpdate = throttle(() => this.update(targetElement, getComputedStyle(targetElement)));
      this.resizeObserver = new ResizeObserver(this.scheduleUpdate.bind(this));
    }

    start(element) {
      if (this.element) {
        this.stop();
      }

      if (element) {
        element.addEventListener("scroll", this.scheduleUpdate);
        this.resizeObserver.observe(element);
        this.element = element;
      }
    }

    stop() {
      if (!this.element) {
        return;
      }

      this.element.removeEventListener("scroll", this.scheduleUpdate);
      this.resizeObserver.unobserve(this.element);
      this.element = null;
    }

    update(targetElement, style) {
      if (!this.element) {
        return;
      }

      const maxSize = style.getPropertyValue("--scroll-shadow-size") ? parseInt(style.getPropertyValue("--scroll-shadow-size")) : 0;
      const scroll = {
        top: Math.max(this.element.scrollTop, 0),
        bottom: Math.max(this.element.scrollHeight - this.element.offsetHeight - this.element.scrollTop, 0),
        left: Math.max(this.element.scrollLeft, 0),
        right: Math.max(this.element.scrollWidth - this.element.offsetWidth - this.element.scrollLeft, 0)
      };

      requestAnimationFrame(() => {
        for (const position of ["top", "bottom", "left", "right"]) {
          targetElement.style.setProperty(
            `--${position}`,
            `${scroll[position] > maxSize ? maxSize : scroll[position]}px`
          );
        }
      });
    }
  };

  var ScrollShadow = class extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" }).innerHTML = template;
      this.updater = new Updater(this.shadowRoot.lastElementChild);
    }

    connectedCallback() {
      this.shadowRoot.querySelector("slot").addEventListener("slotchange", this.start);
      this.start();
    }

    disconnectedCallback() {
      this.updater.stop();
    }

    start() {
      if (this.firstElementChild) {
        this.updater.start(this.firstElementChild);
      }
    }
  };

  if ("ResizeObserver" in window && !window.customElements.get("scroll-shadow")) {
    window.customElements.define("scroll-shadow", ScrollShadow);
  }


  
  /*------------------------------------*\
    #CAROUSELS
  \*------------------------------------*/

  /**
   * Our BaseCarousel. Not currently used by any elements, but we extend this to create other carousels.
   */
  var BaseCarousel = class extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this._reloaded = false;

      if (Shopify.designMode) {
        this.closest(".shopify-section")?.addEventListener("shopify:section:select", (event) => this._reloaded = event.detail.load);
      }

      if (this.items.length > 1) {
        if (Shopify.designMode) {
          this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target), { animate: !event.detail.load }));
        }

        if (this.hasAttribute("adaptive-height")) {
          this.addEventListener("carousel:settle", this._adjustHeight);
          this._adjustHeight();
        }

        this.addEventListener("carousel:select", this._preloadImages, { signal: this._abortController.signal });
        this.addEventListener("carousel:filter", this._filterItems, { signal: this._abortController.signal });
        this.addEventListener("control:prev", this.previous, { signal: this._abortController.signal });
        this.addEventListener("control:next", this.next, { signal: this._abortController.signal });
        this.addEventListener("control:select", (event) => this.select(event.detail.index, event.detail.externalClick), { signal: this._abortController.signal });
      }

      if (this.selectedIndex === 0) {
        this.selectedSlide?.classList.add("is-selected");
        this._dispatchEvent("carousel:select", 0);
      } else {
        this.select(this.selectedIndex, { animate: false, force: true });
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get items() {
      if (this.hasAttribute("reversed")) {
        return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children).reverse();
      } else {
        return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children);
      }
    }

    /**
     * offsetParent checks if the element has a parent. If the item itself is display: none, visibility: hidden, or opacity: 0, then it will not be registered.
     */
    get visibleItems() {
      return this.items.filter((item) => item.offsetParent !== null);
    }

    // If we have no selected slide, we set this as 0
    get selectedIndex() {
      return this._selectedIndex = this._selectedIndex ?? parseInt(this.getAttribute("initial-index") || 0);
    }

    get selectedIndexAmongVisible() {
      return this.visibleItems.indexOf(this.selectedSlide);
    }

    get loop() {
      return false;
    }

    get selectedSlide() {
      return this.items[this.selectedIndex];
    }

    get previousSlide() {
      return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible - 1 + this.visibleItems.length) % this.visibleItems.length : Math.max(this.selectedIndexAmongVisible - 1, 0)];
    }

    get nextSlide() {
      return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible + 1 + this.visibleItems.length) % this.visibleItems.length : Math.min(this.selectedIndexAmongVisible + 1, this.visibleItems.length - 1)];
    }

    previous(animate11 = true) {
      this.select(this.items.indexOf(this.previousSlide), false, { direction: "previous", animate: animate11 });
    }

    next(animate11 = true) {
      this.select(this.items.indexOf(this.nextSlide), false, { direction: "next", animate: animate11 });
    }

    _transitionTo(fromSlide, toSlide, options = {}) {
    }

    _adjustHeight() {
      if (this.hasAttribute("adaptive-height") && this.selectedSlide.clientHeight !== this.clientHeight) {
        this.style.maxHeight = `${this.selectedSlide.clientHeight}px`;
      }
    }

    _filterItems(event) {
      this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
    }

    _preloadImages() {
      [this.previousSlide, this.nextSlide].forEach((item) => {
        if (item) {
          Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
        }
      });
    }

    _dispatchEvent(eventName, index) {
      this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: { slide: this.items[index], index } }));
    }
  };


  /**
   * The EffectCarousel allows us to add specific transitions such as a fade. It essentially functions like a traditional slideshow. We use the `GestureArea` element to listen for swipe events.
   * In many cases, we use the `EffectCarousel` in combination with the `PageDots` element to simulate tabbed functionality such as on the `ImageTabs`, `Timeline` and `MultipleImagesWithText` sections.
   */
  var EffectCarousel = class extends BaseCarousel {
    constructor() {
      super();
      this.addEventListener("carousel:select", this._onSlideSelected);
    }

    connectedCallback() {
      super.connectedCallback();

      if (this.items.length > 1 && this.hasAttribute("autoplay") && window.matchMedia("screen and (min-width: 1000px)").matches) {
        this._player = new Player(this.getAttribute("autoplay"));
        this._player.addEventListener("player:end", this.next.bind(this));
        Motion.inView(this, () => this._player.resume(true));

        if (Shopify.designMode) {
          this.addEventListener("shopify:block:select", () => this._player.stop());
          this.addEventListener("shopify:block:deselect", () => this._player.start());
        }
      }

      if (this.items.length > 1) {
        if (this.swipeable) {
          new GestureArea(this, { signal: this._abortController.signal });
          this.addEventListener("swipeleft", this.next, { signal: this._abortController.signal });
          this.addEventListener("swiperight", this.previous, { signal: this._abortController.signal });
        }
      }
    }

    get player() {
      return this._player;
    }

    get loop() {
      return true;
    }

    get swipeable() {
      return this.getAttribute("swipeable") !== "false";
    }

    get staticMedia() {
      return this.hasAttribute("static-media");
    }

    async select(index, externalClick = false, { direction, animate: animate11 = true } = {}) {
      const indexBeforeChange = this.selectedIndex;
      this._selectedIndex = index;
      this._dispatchEvent("carousel:select", index);

      if (!direction) {
        direction = index > indexBeforeChange ? "next" : "previous";
      }

      if (index !== indexBeforeChange) {
        const [fromSlide, toSlide] = [this.items[indexBeforeChange], this.items[index]];
        this._dispatchEvent("carousel:change", index);
        this._dispatchEvent("effect-carousel:change", index);
        this.player?.pause();

        if (!this.staticMedia) {
          await this._transitionTo(fromSlide, toSlide, { direction, animate: animate11 }); 
        }
        
        if (externalClick) {
          document.querySelector(`[aria-controls="${this.id}"]`)?.setAttribute("autoplay-off", "");
        } else {
          this.player?.resume(true);
        }

        this._dispatchEvent("carousel:settle", index);
      }
    }

    /**
     * Perform a simple fade animation. For more complex animations, you should implement your own custom elements
     * that extends the EffectCarousel, and implement your own transition. You should make sure to return a promise
     * that resolves when the animation is finished
     */
    _transitionTo(fromSlide, toSlide, { direction = "next", animate: animate11 = true } = {}) {
      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");

      /**
       * `step-end` easing means the z-index value will change immediately once
       * everything is complete. We do this to ensure the z-index value isn't
       * being gradually animated.
       */
      const timelineControls = Motion.timeline([
        [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { zIndex: { easing: "step-end" } }],
        [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { at: "<", zIndex: { easing: "step-end" } }]
      ], { duration: animate11 ? 0.3 : 0 });

      return timelineControls.finished;
    }

    async _onSlideSelected(event) {
      Array.from(this.querySelectorAll("video-media")).forEach((video) => video.pause());

      if (event.detail.slide && event.detail.slide.getAttribute("data-slide-type") === "video") {
        const visibleVideo = Array.from(event.detail.slide.querySelectorAll("video-media")).filter((video) => video.offsetParent !== null).pop();

        visibleVideo.play({ restart: true });

        if (this.player) {
          const video = visibleVideo.querySelector("video");

          if (isNaN(video.duration)) {
            await new Promise((resolve) => {
              video.onloadedmetadata = () => resolve();
            });
          }
          
          this._player.setDuration(video.duration);
        }
      } else {
        this._player?.setDuration(parseInt(this.getAttribute("autoplay")));
      }
    }
  };

  if (!window.customElements.get("effect-carousel")) {
    window.customElements.define("effect-carousel", EffectCarousel);
  }

  /**
   * Our scrolling carousel element. Along with users being able to manually scroll, it listens for navigation events which will automatically scroll a user to their selection.
   */
  var ScrollCarousel = class extends BaseCarousel {
    constructor() {
      super();
      if (window.ResizeObserver) {
        new ResizeObserver(throttle(this._adjustHeight.bind(this))).observe(this);
      }
    }

    connectedCallback() {
      this._abortController = new AbortController();
      
      this._hasPendingProgrammaticScroll = false;
      this._scrollArea = new ScrollArea(this, this._abortController);
      super.connectedCallback();

      this.distanceToBleed = getComputedStyle(this).getPropertyValue('padding-inline-start');
      this.addEventListener("scroll", throttle(this._onCarouselScroll.bind(this)), { signal: this._abortController.signal });

      this.addEventListener("scrollend", this._onScrollSettled, { signal: this._abortController.signal });
    }

    /**
     * This is assuming that all items are the same width. It consistently stores itemOffset as the difference
     * between the index 1 and 0 items.
     */
    get itemOffset() {
      return this.visibleItems.length < 2 ? 0 : this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
    }

    /**
     * This also assumes equal width items. It uses the 0 index element to help determine slide size
     */
    get slidesPerPage() {
      return this.visibleItems.length < 2 ? 1 : Math.floor((this.clientWidth - this.visibleItems[0].offsetLeft) / (Math.abs(this.itemOffset) - (parseInt(getComputedStyle(this).gap) || 0)));
    }

    /**
     * As above, the totalPages will be determined by the slidesPerPage which means assuming equal width items.
     */
    get totalPages() {
      return this.visibleItems.length < 2 ? 1 : this.visibleItems.length - this.slidesPerPage + 1;
    }

    select(index, { animate: animate11 = true, force = false } = {}) {
      const indexBeforeChange = this.selectedIndex;

      if (!this.offsetParent || this._scrollArea.scrollDirection === "none") {
        return this._selectedIndex = index;
      }

      const indexAmongVisible = this.visibleItems.indexOf(this.items[index]);
      index = this.items.indexOf(this.visibleItems[Math.min(this.totalPages, indexAmongVisible)]);
      this._selectedIndex = index;
      this._dispatchEvent("carousel:select", index);

      if (index !== indexBeforeChange || force) {
        const [fromSlide, toSlide] = [this.items[indexBeforeChange], this.items[index]];
        this._dispatchEvent("carousel:change", index);
        this._transitionTo(fromSlide, toSlide, { animate: animate11 });
      }
    }

    /**
     * Transition using the scrollTo method. To prevent the intersection observer from catching the change, we set
     * a "hasPendingProgrammaticScroll" variable to true, that is set back to false once the scroll has settled
     */
    _transitionTo(fromSlide, toSlide, { animate: animate11 = true } = {}) {
      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");

      let slideAlign = this._extractSlideAlign(toSlide);
      let scrollAmount = 0;

      switch (slideAlign) {
        case "start":
          // Assuming equal sized items. It simply gets the offset and multiplies by the index of the new slide
          scrollAmount = this.itemOffset * this.visibleItems.indexOf(toSlide);

          /* The below is commented out but works if we ever have different sized items */
          // scrollAmount = toSlide.offsetLeft - parseInt(this.distanceToBleed);
          break;
        case "center":
          scrollAmount = toSlide.offsetLeft - (this.clientWidth / 2 - (parseInt(getComputedStyle(this).scrollPaddingInline) || 0)) + toSlide.clientWidth / 2;
          break;
      }

      this._hasPendingProgrammaticScroll = animate11;
      this.scrollTo({ left: scrollAmount, behavior: animate11 ? "smooth" : "auto" });
    }

    /**
     * Update the index when manually scrolling (which allows to update the controls)
     */
    _onCarouselScroll() {
      if (this._hasPendingProgrammaticScroll || this._scrollArea.scrollDirection === "none") {
        return;
      }

      const newIndex = this.items.indexOf(this.visibleItems[Math.round(this.scrollLeft / this.itemOffset)]);

      if (newIndex !== this.selectedIndex) {
        this._selectedIndex = newIndex;
        this._dispatchEvent("carousel:select", this.selectedIndex);
        this._dispatchEvent("carousel:change", this.selectedIndex);
      }
    }

    /**
     * On the scroll has settled we dispatch the event (which covers both programmatic scroll and swipe)
     */
    _onScrollSettled() {
      this.items.forEach((item) => item.classList.remove("is-selected"));
      this.selectedSlide.classList.add("is-selected");
      this._hasPendingProgrammaticScroll = false;
      this._dispatchEvent("carousel:settle", this.selectedIndex);
    }

    _adjustHeight() {
      this.style.maxHeight = null;
      if (this._scrollArea.scrollDirection !== "none") {
        super._adjustHeight();
      }
    }

    _extractSlideAlign(slide) {
      return getComputedStyle(slide).scrollSnapAlign === "center" ? "center" : "start";
    }
  };

  if (!window.customElements.get("scroll-carousel")) {
    window.customElements.define("scroll-carousel", ScrollCarousel);
  }




  /*------------------------------------*\
    #INTERACTION ELEMENTS
  \*------------------------------------*/

  /**
   * This replaces the existing button temporarily with a loading animation providing feedback to the user. Used for things such as Add to Cart.
   */
  var CustomButton = class extends HTMLButtonElement {
    static get observedAttributes() {
      return ["aria-busy"];
    }

    constructor() {
      super();
      if (this.type === "submit" && this.form) {
        this.form.addEventListener("submit", () => this.setAttribute("aria-busy", "true"));
      }

      this.append(this.contentElement, this.animationElement);

      // Ensure the animation isn't running on back/forward page cachae
      window.addEventListener("pageshow", () => this.removeAttribute("aria-busy"));
    }

    get contentElement() {
      if (this._contentElement) {
        return this._contentElement;
      }

      this._contentElement = document.createElement("div");
      this._contentElement.append(...this.childNodes);
      return this._contentElement = this._contentElement || document.createElement("div").append();
    }

    get animationElement() {
      return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
        <span class="button__loader">
          <span></span>
          <span></span>
          <span></span>
        </span>
      `).firstElementChild;
    }

    async attributeChangedCallback(name, oldValue, newValue) {
      if (newValue === "true") {
        /**
         * We hide the content element, and show the animation element
         */
        Motion.timeline([
          [this.contentElement, { transform: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15 }],
          [this.animationElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: 1 }, { duration: 0.15 }]
        ]);

        /**
         * We run a complex loading animation which contains 3 dots which will essentially pulse.
         */
        Motion.animate(this.animationElement.children, { opacity: [1, 0.1] }, { duration: 0.35, delay: Motion.stagger(0.35 / 3), direction: "alternate", repeat: Infinity });
      } else {
        /**
         * We hide the animation element and show the content element
         */
        Motion.timeline([
          [this.animationElement, { transform: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15 }],
          [this.contentElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: 1 }, { duration: 0.15 }]
        ]);
      }
    }
  };

  if (!window.customElements.get("custom-button")) {
    window.customElements.define("custom-button", CustomButton, { extends: "button" });
  }



  /**
   * A standard Tabbing element. This primarily used within our SearchDrawer component. In most cases, areas that look like tabs are often using a combination of `EffectCarousel` and `PageDots` elements.
   */
  var Tabs = class extends HTMLElement {
    static get observedAttributes() {
      return ["selected-index"];
    }

    constructor() {
      super();
      if (!this.shadowRoot) {
        this.attachShadow({ mode: "open" }).appendChild(this.querySelector("template").content.cloneNode(true));
      }

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.selectedIndex = this.buttons.indexOf(event.target));
      }
      this.addEventListener("keydown", this._handleKeyboard);
    }

    connectedCallback() {
      this._abortController = new AbortController();

      this.buttons = Array.from(this.shadowRoot.querySelector('slot[name="title"]').assignedNodes(), (item) => item.matches("button") && item || item.querySelector("button"));

      this.panels = Array.from(this.shadowRoot.querySelector('slot[name="content"]').assignedNodes());

      this.buttons.forEach((button, index) => button.addEventListener("click", () => this.selectedIndex = index, { signal: this._abortController.signal }));

      this.selectedIndex = this.selectedIndex;
      this._setupAccessibility();
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get animationDuration() {
      return this.hasAttribute("animation-duration") ? parseFloat(this.getAttribute("animation-duration")) : 0.15;
    }

    get selectedIndex() {
      return parseInt(this.getAttribute("selected-index")) || 0;
    }

    set selectedIndex(index) {
      this.setAttribute("selected-index", Math.min(Math.max(index, 0), this.buttons.length - 1).toString());

      this.style.setProperty("--selected-index", this.selectedIndex.toString());

      this.style.setProperty("--item-count", this.buttons.length.toString());
    }

    attributeChangedCallback(name, oldValue, newValue) {
      this.buttons.forEach((button, index) => button.setAttribute("aria-selected", index === parseInt(newValue) ? "true" : "false"));

      if (name === "selected-index" && oldValue !== null && oldValue !== newValue) {
        this._transition(this.panels[parseInt(oldValue)], this.panels[parseInt(newValue)]);
      }
    }

    _setupAccessibility() {
      const componentID = crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4);

      this.buttons.forEach((button, index) => {
        button.setAttribute("role", "tab");
        button.setAttribute("aria-controls", `tab-panel-${componentID}-${index}`);
        button.id = `tab-${componentID}-${index}`;
      });

      this.panels.forEach((panel, index) => {
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", `tab-${componentID}-${index}`);
        panel.id = `tab-panel-${componentID}-${index}`;
        panel.hidden = index !== this.selectedIndex;
      });
    }

    /**
     * As per https://www.w3.org/WAI/ARIA/apg/example-index/tabs/tabs-automatic.html, when a tab is currently focused,
     * left and right arrow should switch the tab
     */
    _handleKeyboard(event) {
      const index = this.buttons.indexOf(document.activeElement);

      if (index === -1 || !["ArrowLeft", "ArrowRight"].includes(event.key)) {
        return;
      }

      if (event.key === "ArrowLeft") {
        this.selectedIndex = (this.selectedIndex - 1 + this.buttons.length) % this.buttons.length;
      } else {
        this.selectedIndex = (this.selectedIndex + 1 + this.buttons.length) % this.buttons.length;
      }

      this.buttons[this.selectedIndex].focus();
    }

    /**
     * Perform a custom transition (can be overridden in subclasses). To "from" and "to" are hash representing the panel
     */
    async _transition(fromPanel, toPanel) {
      await Motion.animate(fromPanel, { opacity: [1, 0] }, { duration: this.animationDuration }).finished;
      fromPanel.hidden = true;
      toPanel.hidden = false;
      await Motion.animate(toPanel, { opacity: [0, 1] }, { duration: this.animationDuration }).finished;
    }
  };

  if (!window.customElements.get("x-tabs")) {
    window.customElements.define("x-tabs", Tabs);
  }
  
  function triggerEvent(element, name, data = {}) {
    element.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: data
    }));
  }


  var AnimatedDetails = class extends HTMLDetailsElement {
    constructor() {
      super();
      this.summaryElement = this.firstElementChild;
      this.contentElement = this.lastElementChild;
      this._open = this.hasAttribute("open");

      this.summaryElement.addEventListener("click", this._onSummaryClicked.bind(this));
      
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", () => this.open = true);
        this.addEventListener("shopify:block:deselect", () => this.open = false);
      }
    }

    set open(value) {
      if (value !== this._open) {
        this._open = value;
        if (this.isConnected) {
          this._transition(value);
        } else {
          value ? this.setAttribute("open", "") : this.removeAttribute("open");
        }
      }
    }

    get open() {
      return this._open;
    }

    _onSummaryClicked(event) {
      event.preventDefault();
      this.open = !this.open;
    }

    _transition(value) {
    }
  };


  var AccordionDisclosure = class extends AnimatedDetails {
    static get observedAttributes() {
      return ["open"];
    }

    constructor() {
      super();
      this.setAttribute("aria-expanded", this._open ? "true" : "false");
    }

    set open(value) {
      super.open = value;
      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get open() {
      return super.open;
    }

    async _transition(value) {
      this.style.overflow = "hidden";
      if (value) {
        this.setAttribute("open", "");
        await Motion.timeline([
          [this, { height: [`${this.summaryElement.clientHeight}px`, `${this.scrollHeight}px`] }, { duration: 0.25, easing: "ease" }],
          [this.contentElement, { opacity: [0, 1], transform: ["translateY(0)", "translateY(-4px)"] }, { duration: 0.15, at: "-0.1" }]
        ]).finished;
      } else {
        await Motion.timeline([
          [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
          [this, { height: [`${this.clientHeight}px`, `${this.summaryElement.clientHeight}px`] }, { duration: 0.25, at: "<", easing: "ease" }]
        ]).finished;
        this.removeAttribute("open");
      }

      this.style.height = "auto";
      this.style.overflow = "visible";
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "open") {
        this.setAttribute("aria-expanded", newValue === "" ? "true" : "false");
      }
    }
  };

  if (!window.customElements.get("accordion-disclosure")) {
    window.customElements.define("accordion-disclosure", AccordionDisclosure, { extends: "details" });
  }
  
  /**
   * A toggle button that allows us to open elements externally. E.g. Our Shop The Look dot buttons control opening of accordions.
   */
  var ToggleButton = class extends HTMLButtonElement {
    static get observedAttributes() {
      return ["aria-expanded"];
    }

    constructor() {
      super();
      this.addEventListener("click", this._onButtonClick.bind(this));
    }

    connectedCallback() {
      document.addEventListener("openable-element:open", (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = true;
          event.stopPropagation();
        }
      });

      document.addEventListener("openable-element:close", (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = false;
          event.stopPropagation();
        }
      });
    }

    _onButtonClick(event, stayOpen) {
      // This allows us to disable closing of the active element. E.g. on a Shop the Look section, if we have element 1 open, then clicking on dot 1 shouldn't close this.
      if (this.disableToggleIfOpen) {
        // If the current element is already active, we scroll to it
        if (this.isExpanded && this.controlledElement.scrollToActive) {
          this.controlledElement.scrollToActiveElement(this.controlledElement);
        } else {
          // If the element isn't active, we open it
          this.controlledElement._onSummaryClicked(event, this.controlledElement);
        }
      } else {
        if (!stayOpen && this.isExpanded) {
          this.isExpanded = !this.isExpanded;
        } else {
          this.isExpanded = true;
        }
      }
    }

    get isExpanded() {
      return this.getAttribute("aria-expanded") === "true";
    }

    set isExpanded(value) {
      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get controlledElement() {
      return document.getElementById(this.getAttribute("aria-controls"));
    }

    get disableToggleIfOpen() {
      return this.hasAttribute("disable-toggle-if-open");
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "aria-expanded":
          if (oldValue === "false" && newValue === "true") {
            this.controlledElement.open = true;
          } else if (oldValue === "true" && newValue === "false") {
            this.controlledElement.open = false;
          }
          break;
      }
    }
  };

  if (!window.customElements.get("toggle-button")) {
    window.customElements.define("toggle-button", ToggleButton, { extends: "button" });
  }

  /*------------------------------------*\
    #PRODUCT TAB ACCORDIONS
  \*------------------------------------*/

  /**
   * The product pages use accordions on small screens and tabs on larger screens. 
   * We could have accomplished this using existing code by creating separate tabs and accordion elements but we don't want to duplicate content. Hence the unique functionality below.
   */
  var ProductTabAccordionContent = class extends HTMLElement {
    static get observedAttributes() {
      return ["open"];
    }

    constructor() {
      super();
      this._open = this.hasAttribute("open");

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", () => this.open = true);
        this.addEventListener("shopify:block:deselect", () => this.open = false);
      }
    }

    set open(value) {
      if (value !== this._open) {
        this._open = value;
        if (this.isConnected) {
          this._transition(value);
        } else {
          value ? this.setAttribute("open", "") : this.removeAttribute("open");
        }
      }

      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get open() {
      return this.hasAttribute("open");
    }

    async _transition(value) {
      this.style.overflow = "hidden";

      if (value) {
        this.setAttribute("open", "");
        await Motion.timeline([
          [this, { height: [`${this.clientHeight}px`, `${this.scrollHeight}px`] }, { duration: 0.25, easing: "ease"}]
        ]).finished;
        // Fixed height is required for the animation. We then set to auto height.
        this.style.height = "auto";
      } else {
        await Motion.timeline([
          [this, { height: [`${this.clientHeight}px`, `0px`] }, { duration: 0.25, at: "<", easing: "ease" }]
        ]).finished;
        this.removeAttribute("open");
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "open" && newValue === "") {
        this.setAttribute("aria-expanded", "true");
        if (this.id == "resources-tab-content" && this.querySelector(".resources__articles")) {
          let element = this.querySelector(".resources__articles");
          
          let tagHandle = element.getAttribute("tag-handle") || undefined;
          let numberOfPosts = parseInt(element.getAttribute("number-of-posts"));
            
          _fetchBlogPosts(tagHandle, element, numberOfPosts);
        }
      } else {
        this.setAttribute("aria-expanded", "false");
      }
    }
    
  }

  if (!window.customElements.get("product-tab-accordion-content")) {
    window.customElements.define("product-tab-accordion-content", ProductTabAccordionContent);
  }

  function _fetchBlogPosts(tagHandle, element, numberOfPosts) {
    if (!Shopify.designMode) {
      if (!tagHandle) {
        return;
      }
    }

    try {
      return _loadBlog(tagHandle, element, numberOfPosts);
    } catch (e) {
      if (e.name !== "AbortError") {
        throw e;
      }
    }
  }

  async function _loadBlog(tagHandle, element, numberOfPosts) {
    const newElement = new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}blogs/support-advice/tagged/${tagHandle}?view=fetch&section_id=fetch-blog`)).text(), "text/html");

    const blogPostsResults = element.querySelector(".blog-post-list");
    const blogResultsChildren = newElement.querySelector(".blog-results").children;
    const buttonWrapper = element.querySelector(".blog-posts__button-wrapper");

    blogPostsResults.innerHTML = "";
    let i = 0;
    const postsLimit = numberOfPosts || blogResultsChildren.length;
    
    for (const child of blogResultsChildren) {
      if (i >= postsLimit) break;
      blogPostsResults.appendChild(document.importNode(child, true));
      i++;
    }

    buttonWrapper.style.opacity = "1";

    element.setAttribute("blog-posts-loaded","");
  }


  /**
   * This controls the tab functionality within the Product Tabs/Accordion section
   */
  var TabsNav = class extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll("button[aria-controls]"));

      this.buttons.forEach((button) => button.addEventListener("click", () => this.selectButton(button)));

      this.addEventListener("shopify:block:select", (event) => this.selectButton(event.target, !event.detail.load));
    }

    get selectedTabIndex() {
      return this.buttons.findIndex((button) => button.getAttribute("aria-expanded") === "true");
    }

    get selectedButton() {
      return this.buttons.find((button) => button.getAttribute("aria-expanded") === "true");
    }

    selectButton(button, animate = true) {
      if (!this.buttons.includes(button) || this.selectedButton === button) {
        return;
      }

      if (button.getAttribute("aria-controls") == "resources-tab" && document.querySelector("#resources-tab .resources__articles")) {
        let element = document.querySelector("#resources-tab .resources__articles");
        let tagHandle = element.getAttribute("tag-handle");
        let numberOfPosts = parseInt(element.getAttribute("number-of-posts"));

        _fetchBlogPosts(tagHandle, element, numberOfPosts);
      }

      const from = document.getElementById(this.selectedButton.getAttribute("aria-controls"));
       
      const to = document.getElementById(button.getAttribute("aria-controls"));

      if (animate) {
        this._transitionContent(from, to);
      } else {
        from.hidden = true;
        to.hidden = false;
      }

      this.selectedButton.setAttribute("aria-expanded", "false");

      button.setAttribute("aria-expanded", "true");
    }

    async _transitionContent(from, to) {
      await Motion.animate(from, { opacity: [1, 0] }, { duration: 0.25 }).finished;
      from.hidden = true;
      to.hidden = false;
      await Motion.animate(to, { opacity: [0, 1] }, { duration: 0.25 }).finished;
    }
  };

  window.customElements.define("tabs-nav", TabsNav);


  /**
   * This allows us to open product/tabs accordions using an external link, and scroll down to the opened section. For example, the `more info` link on the product page will open the details tab/accordion and scroll the user down to it.
   */
  var ProductTabAccordionScroller = class extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener("click", this._onButtonClick.bind(this));
    }

    connectedCallback() {
      this.tabsNav = document.querySelector("tabs-nav");
      this.tabButton = document.querySelector('[aria-controls="' + this.tabLink + '"');
      this.accordionButton = document.querySelector('[aria-controls="' + this.tabLink + '-content"]');
    }

    get tabLink() {
      return this.getAttribute("tab-link");
    }

    _onButtonClick(event) {
      event.preventDefault();
      if (this.tabButton) {
        this.tabsNav.selectButton(this.tabButton);
      }

      if (this.accordionButton) {
        // Pass in true for externalClick parameter. This ensures we don't close the accordion on multiple clicks from external scroller link.
        this.accordionButton._onButtonClick(event, true);
      }

      const scrollToElement = window.matchMedia("(max-width: 999px)").matches ? this.accordionButton : this.tabsNav;

      scrollToElement.scrollIntoView({ behavior: "smooth"});
    }
  }

  if (!window.customElements.get("product-tab-accordion-scroller")) {
    window.customElements.define("product-tab-accordion-scroller", ProductTabAccordionScroller, { extends: "button" }); 
  }



  /*------------------------------------*\
    #DIALOG ELEMENTS
  \*------------------------------------*/

  var MegaMenuDisclosure = class extends AnimatedDetails {
    constructor() {
      super();
      this._detectClickOutsideListener = this._detectClickOutside.bind(this);
      this._detectEscKeyboardListener = this._detectEscKeyboard.bind(this);
      this._detectFocusOutListener = this._detectFocusOut.bind(this);
      this._detectHoverListener = this._detectHover.bind(this);
      this._hoverTimer = null;
      this.addEventListener("mouseover", this._detectHoverListener.bind(this));
      this.addEventListener("mouseout", this._detectHoverListener.bind(this));
    }

    get trigger() {
      return !window.matchMedia("screen and (pointer: fine)").matches ? "click" : this.getAttribute("trigger");
    }

    get mouseOverDelayTolerance() {
      return 250;
    }

    /**
     * When the summary is clicked and the mode is hover, we follow the link (if set) that may have been specified
     * on the parent. If the mode is click, we defer to the default behavior of the "AnimatedDetails" parent
     */
    _onSummaryClicked(event) {
      if (this.trigger === "hover") {
        event.preventDefault();
        if (event.currentTarget.hasAttribute("data-url")) {
          window.location.href = event.currentTarget.getAttribute("data-url");
        }
      } else {
        super._onSummaryClicked(event);
      }
    }

    async _transition(value) {
      if (value) {
        this.setAttribute("open", "");
        document.addEventListener("click", this._detectClickOutsideListener);
        document.addEventListener("keydown", this._detectEscKeyboardListener);
        document.addEventListener("focusout", this._detectFocusOutListener);
        const openSiblings = Array.from(this.closest("ul").querySelectorAll("[open]")).filter((item) => item !== this);
        openSiblings.forEach((sibling) => sibling.open = false);
        await this._transitionIn(openSiblings.length > 0);
      } else {
        document.removeEventListener("click", this._detectClickOutsideListener);
        document.removeEventListener("keydown", this._detectEscKeyboardListener);
        document.removeEventListener("focusout", this._detectFocusOutListener);
        await this._transitionOut();
        this.removeAttribute("open");
      }
    }

    _transitionIn(hasOpenSiblings = false) {
      document.body.classList.add("menu-open"); // Add overlay
      Motion.animate(
        this.contentElement, 
        { opacity: 1 },
        { duration: 0.3 }
      );
    }

    _transitionOut() {
      Motion.animate(
        this.contentElement, 
        { opacity: 0 },
        { duration: 0.3 }
      );
      document.body.classList.remove("menu-open"); // Remove overlay
    }

    /**
     * When dropdown menu is configured to open on click, we add a listener to detect click outside and automatically
     * close the navigation.
     */
    _detectClickOutside(event) {
      if (this.trigger !== "click") {
        return;
      }
      if (!this.contains(event.target) && !(event.target.closest("details") instanceof _DropdownDisclosure)) {
        this.open = false;
      }
    }

    /**
     * On desktop, if the mode is set to hover, we open/close the dropdown on hover
     */
    _detectHover(event) {
      if (this.trigger !== "hover") {
        return;
      }
      if (event.type === "mouseover") {
        this.open = true;
        clearTimeout(this._hoverTimer);
      } else {
        this._hoverTimer = setTimeout(() => this.open = false, this.mouseOverDelayTolerance);
      }
    }

    /**
     * Detect if we hit the "Escape" key to automatically close the dropdown
     */
    _detectEscKeyboard(event) {
      if (event.code === "Escape") {
        const targetMenu = event.target.closest("details[open]");
        if (targetMenu) {
          targetMenu.open = false;
        }
      }
    }

    /**
     * Close the dropdown automatically when the dropdown is focused out
     */
    _detectFocusOut(event) {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) {
        this.open = false;
      }
    }
  };

  if (!window.customElements.get("mega-menu-disclosure")) {
    window.customElements.define("mega-menu-disclosure", MegaMenuDisclosure, { extends: "details" });
  }

  var AccountMenuDisclosure = class extends AnimatedDetails {
    constructor() {
      super();
      this._detectClickOutsideListener = this._detectClickOutside.bind(this);
      this._detectEscKeyboardListener = this._detectEscKeyboard.bind(this);
      this._detectFocusOutListener = this._detectFocusOut.bind(this);
      this._detectHoverListener = this._detectHover.bind(this);
      this._hoverTimer = null;
      this.addEventListener("mouseover", this._detectHoverListener.bind(this));
      this.addEventListener("mouseout", this._detectHoverListener.bind(this));
    }

    get trigger() {
      return !window.matchMedia("screen and (pointer: fine)").matches ? "click" : this.getAttribute("trigger");
    }

    get mouseOverDelayTolerance() {
      return 250;
    }

    /**
     * When the summary is clicked and the mode is hover, we follow the link (if set) that may have been specified
     * on the parent. If the mode is click, we defer to the default behavior of the "AnimatedDetails" parent
     */
    _onSummaryClicked(event) {
      if (this.trigger === "hover") {
        event.preventDefault();
        if (event.currentTarget.hasAttribute("data-url")) {
          window.location.href = event.currentTarget.getAttribute("data-url");
        }
      } else {
        super._onSummaryClicked(event);
      }
    }

    async _transition(value) {
      if (value) {
        this.setAttribute("open", "");
        document.addEventListener("click", this._detectClickOutsideListener);
        document.addEventListener("keydown", this._detectEscKeyboardListener);
        document.addEventListener("focusout", this._detectFocusOutListener);
        await this._transitionIn();
      } else {
        document.removeEventListener("click", this._detectClickOutsideListener);
        document.removeEventListener("keydown", this._detectEscKeyboardListener);
        document.removeEventListener("focusout", this._detectFocusOutListener);
        await this._transitionOut();
        this.removeAttribute("open");
      }
    }

    _transitionIn() {
      Motion.animate(
        this.contentElement, 
        { opacity: 1 },
        { duration: 0.3 }
      );
    }

    _transitionOut() {
      Motion.animate(
        this.contentElement, 
        { opacity: 0 },
        { duration: 0.3 }
      );
    }

    /**
     * When dropdown menu is configured to open on click, we add a listener to detect click outside and automatically
     * close the navigation.
     */
    _detectClickOutside(event) {
      if (this.trigger !== "click") {
        return;
      }
      if (!this.contains(event.target) && !(event.target.closest("details") instanceof _DropdownDisclosure)) {
        this.open = false;
      }
    }

    /**
     * On desktop, if the mode is set to hover, we open/close the dropdown on hover
     */
    _detectHover(event) {
      if (this.trigger !== "hover") {
        return;
      }
      if (event.type === "mouseover") {
        this.open = true;
        clearTimeout(this._hoverTimer);
      } else {
        this._hoverTimer = setTimeout(() => this.open = false, this.mouseOverDelayTolerance);
      }
    }

    /**
     * Detect if we hit the "Escape" key to automatically close the dropdown
     */
    _detectEscKeyboard(event) {
      if (event.code === "Escape") {
        const targetMenu = event.target.closest("details[open]");
        if (targetMenu) {
          targetMenu.open = false;
        }
      }
    }

    /**
     * Close the dropdown automatically when the dropdown is focused out
     */
    _detectFocusOut(event) {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) {
        this.open = false;
      }
    }
  };

  if (!window.customElements.get("account-menu-disclosure")) {
    window.customElements.define("account-menu-disclosure", AccountMenuDisclosure, { extends: "details" });
  }

  var CloseButton = class extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("dialog:force-close", { bubbles: true, cancelable: true, composed: true })));
    }
  };

  if (!window.customElements.get("close-button")) {
    window.customElements.define("close-button", CloseButton, { extends: "button" });
  }


  /**
   * A base dialog element that powers drawers, modals, popovers etc.
   */
  var DialogElement = class extends HTMLElement {
    static get observedAttributes() {
      return ["id", "open"];
    }

    static #lockLayerCount = 0;
    #isLocked = false;

    constructor() {
      super();

      if (this.shadowDomTemplate) {
        this.attachShadow({ mode: "open" }).appendChild(document.getElementById(this.shadowDomTemplate).content.cloneNode(true));

        const style = document.createElement('style');
        style.textContent = `
          button {
            color: rgb(var(--close-button-color, black));
          }
    
          svg {
            fill: rgb(var(--close-button-icon-color, black));
            stroke: rgb(var(--close-button-icon-color, black));
          }
    
          button:hover {
            --close-button-color: var(--close-button-hover-color, blue);
            --close-button-icon-color: var(--close-button-icon-hover-color, red);
          }
        `;
    
        // Append styles and template into the Shadow DOM
        this.shadowRoot.appendChild(style);
        
        this.shadowRoot.addEventListener("slotchange", (event) => this._updateSlotVisibility(event.target));
      }
      // Force closing of dialog and ensure not to propagate to other dialogs
      this.addEventListener("dialog:force-close", (event) => {
        this.hide();
        event.stopPropagation();
      });
    }

    connectedCallback() {
      // Check if we have an id element
      if (this.id) {
        // As these elements can be added/removed from the DOM, we need to run delegate.off() first before reattaching
        this.delegate.off().on("click", `[aria-controls="${this.id}"]`, this._onToggleClicked.bind(this));
      }

      this._abortController = new AbortController();

      // Accessibility
      this.setAttribute("role", "dialog");

      if (this.shadowDomTemplate) {
        // Click on overlay to close
        this.getShadowPartByName("overlay")?.addEventListener("click", this.hide.bind(this), { signal: this._abortController.signal });
  
        // Update slot visibility
        Array.from(this.shadowRoot.querySelectorAll("slot")).forEach((slot) => this._updateSlotVisibility(slot));
      }

      // Shopify theme editor events
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.show(!event.detail.load), { signal: this._abortController.signal });
        this.addEventListener("shopify:block:deselect", this.hide, { signal: this._abortController.signal });
        this._shopifySection = this._shopifySection || this.closest(".shopify-section");

        if (this._shopifySection) {
          if (this.hasAttribute("handle-section-events")) {
            this._shopifySection.addEventListener("shopify:section:select", (event) => this.show(!event.detail.load), { signal: this._abortController.signal });
            this._shopifySection.addEventListener("shopify:section:deselect", this.hide.bind(this), { signal: this._abortController.signal });
          }

          this._shopifySection.addEventListener("shopify:section:unload", () => this.remove(), { signal: this._abortController.signal });
        }
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
      this.delegate.off();
      this.focusTrap?.deactivate({ onDeactivate: false });

      if (this.#isLocked) {
        this.#isLocked = false;
        document.documentElement.classList.toggle("lock", --DialogElement.#lockLayerCount > 0);
      }
    }

    // animate11 is just a convenient variable flag rather than using `animate` which could overlap with other JS
    show(animate11 = true) {
      if (this.open) {
        return;
      }

      // If no animation, we set an attribute open attribute of immediate
      this.setAttribute("open", animate11 ? "" : "immediate");

      // Once show is complete, we dispatch this event
      return waitForEvent(this, "dialog:after-show");
    }

    hide() {
      if (!this.open) {
        return;
      }

      this.removeAttribute("open");

      // Once hide is complete, we dispatch this event
      return waitForEvent(this, "dialog:after-hide");
    }

    // Uses the Delegate library in vendor.min.js
    get delegate() {
      return this._delegate = this._delegate || new Delegate(document.body);
    }

    get controls() {
      return Array.from(this.getRootNode().querySelectorAll(`[aria-controls="${this.id}"]`));
    }

    get open() {
      return this.hasAttribute("open");
    }

    get shouldTrapFocus() {
      return true;
    }

    get shouldLock() {
      return false;
    }

    /**
     * Sometimes (especially for drawers) we need to ensure an element is on top of everything else. To do so,
     * we need to move the element to the body. We do this on open, and then restore the initial DOM position on
     * close.
     */
    get shouldAppendToBody() {
      return false;
    }

    // We can set an element to have focus on open
    get initialFocus() {
      return this.hasAttribute("initial-focus") ? this.getAttribute("initial-focus") : this.hasAttribute("tabindex") ? this : this.querySelector('input:not([type="hidden"])') || false;
    }

    get preventScrollWhenTrapped() {
      return true;
    }

    // Uses the FocusTrap library in vendor.min.js. This handles a lot of accessibility needs.
    get focusTrap() {
      return this._focusTrap = this._focusTrap || new focusTrap.createFocusTrap(this, {
        onDeactivate: this.hide.bind(this),
        allowOutsideClick: false,
        clickOutsideDeactivates: true,
        initialFocus: window.matchMedia("screen and (pointer: fine)").matches ? this.initialFocus : false,
        fallbackFocus: this,
        preventScroll: this.preventScrollWhenTrapped
      });
    }

    // New
    get shadowDomTemplate() {
      return this.getAttribute("template");
    }

    // New allows getting shadow parts
    getShadowPartByName(name) {
      return this.shadowRoot?.querySelector(`[part="${name}"]`);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "id":
          // If id changes, we reset our main event listener
          if (newValue) {
            this.delegate.off().on("click", `[aria-controls="${this.id}"]`, this._onToggleClicked.bind(this));
          }
          break;
        case "open":
          // Set our aria on our controlling elements
          this.controls.forEach((toggle) => toggle.setAttribute("aria-expanded", newValue === null ? "false" : "true"));

          if (oldValue === null && (newValue === "" || newValue === "immediate")) {
            // If we open

            // Remove inert so the element can be interacted with
            this.removeAttribute("inert");

            // If we append to body, we run this code to get the parent so we can return the element there on close.
            this._originalParentBeforeAppend = null;

            if (this.shouldAppendToBody && this.parentElement !== document.body) {
              this._originalParentBeforeAppend = this.parentElement;
              document.body.append(this);
            }

            // If we have an animation we dispatch an event
            const showTransitionPromise = this._showTransition(newValue !== "immediate") || Promise.resolve();

            // We ensure to dispatch the event once any animation is complete
            showTransitionPromise.then(() => {
              this.dispatchEvent(new CustomEvent("dialog:after-show", { bubbles: true }));
            });

            // Ensure that any transition is complete before trapping focus
            if (this.shouldTrapFocus) {
              this.focusTrap.activate({
                checkCanFocusTrap: () => showTransitionPromise
              });
            }

            // Locks the body element from scrolling
            if (this.shouldLock) {
              DialogElement.#lockLayerCount += 1;
              this.#isLocked = true;
              document.documentElement.classList.add("lock");
            }
          } else if (oldValue !== null && newValue === null) {
            // If we close

            // Ensure the element cannot be interacted with
            this.setAttribute("inert", "");

            // Run hide transition
            const hideTransitionPromise = this._hideTransition() || Promise.resolve();

            hideTransitionPromise.then(() => {
              // Return element back to it's parent if it was appended
              if (this.parentElement === document.body && this._originalParentBeforeAppend) {
                this._originalParentBeforeAppend.appendChild(this);
                this._originalParentBeforeAppend = null;
              }

              // Dispatch event after transition ends
              this.dispatchEvent(new CustomEvent("dialog:after-hide", { bubbles: true }));
            });

            // Deactivates focus once transition has ended
            this.focusTrap?.deactivate({
              checkCanReturnFocus: () => hideTransitionPromise
            });

            // Removes scroll locking from body
            if (this.shouldLock) {
              this.#isLocked = false;
              document.documentElement.classList.toggle("lock", --DialogElement.#lockLayerCount > 0);
            }
          }
          this.dispatchEvent(new CustomEvent("toggle", { bubbles: true }));
          break;
      }
    }

    /* Those methods are used to perform custom show/hide transition, and must return a promise */
    _showTransition(animate11 = true) {
    }

    _hideTransition() {
    }

    /* By default, a focus element is deactivated when you click outside it */
    _allowOutsideClick(event) {
      if ("TouchEvent" in window && event instanceof TouchEvent) {
        return this._allowOutsideClickTouch(event);
      } else {
        return this._allowOutsideClickMouse(event);
      }
    }

    _allowOutsideClickTouch(event) {
      event.target.addEventListener("touchend", (subEvent) => {
        const endTarget = document.elementFromPoint(subEvent.changedTouches.item(0).clientX, subEvent.changedTouches.item(0).clientY);
        if (!this.contains(endTarget)) {
          this.hide();
        }
      }, { once: true });
      return false;
    }

    _allowOutsideClickMouse(event) {
      if (event.type !== "click") {
        return false;
      }

      if (!this.contains(event.target)) {
        this.hide();
      }

      let target = event.target, closestControl = event.target.closest("[aria-controls]");

      if (closestControl && closestControl.getAttribute("aria-controls") === this.id) {
        target = closestControl;
      }

      return this.id !== target.getAttribute("aria-controls");
    }

    _onToggleClicked(event) {
      event?.preventDefault();
      this.open ? this.hide() : this.show();
    }

    _updateSlotVisibility(slot) {
      if (!["header", "footer", "body"].includes(slot.name)) {
        return;
      }
      slot.parentElement.hidden = slot.assignedElements({ flatten: true }).length === 0;
    }
  };

  if (!window.customElements.get("dialog-element")) {
    window.customElements.define("dialog-element", DialogElement);
  }


  /**
   * A modal is an impactful popup that traditionally opens in the centre of the screen. We use this for our product page tooltips.
   */
  var Modal = class extends DialogElement {
    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback();
      this.setAttribute("aria-modal", "true");
    }

    get shadowDomTemplate() {
      return this.getAttribute("template") || "modal-default-template";
    }

    get shouldLock() {
      return true;
    }

    get shouldAppendToBody() {
      return true;
    }

    _showTransition(animate11 = true) {
      let animationControls;
      animationControls = Motion.timeline([[this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }]])

      animate11 ? animationControls.play() : animationControls.finish();
    }

    _hideTransition() {
      let animationControls;

      animationControls = Motion.timeline([[this, { opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }]])

      return animationControls.finished;
    }
  }

  if (!window.customElements.get("x-modal")) {
    window.customElements.define("x-modal", Modal);
  }


  /**
   * A drawer element traditionally slides in or out of view. Used for our mobile menu, search drawer, and cart drawer.
   */
  var reduceDrawerAnimation = window.matchMedia("(prefers-reduced-motion: reduce)").matches || JSON.parse("false");

  var Drawer = class extends DialogElement {
    constructor() {
      super();
    }

    get shadowDomTemplate() {
      return this.getAttribute("template") || "drawer-default-template";
    }

    get shouldLock() {
      return true;
    }

    get shouldAppendToBody() {
      return true;
    }

    get openFrom() {
      return this.getAttribute("open-from") || "right";
    }


    connectedCallback() {
      super.connectedCallback?.();
      this.addEventListener("click", this._handleAnchorClick.bind(this));
    }
  
    disconnectedCallback() {
      super.disconnectedCallback?.();
      this.removeEventListener("click", this._handleAnchorClick.bind(this));
    }
  
    _handleAnchorClick(event) {
      const anchor = event.target.closest("a");
      if (anchor && anchor.getAttribute("href")?.startsWith("#")) {
        this.hide();
      }
    }

    _getClipPathProperties() {
      switch (this.openFrom) {
        case "left":
          return ["inset(0 100% 0 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
        case "right":
          return ["inset(0 0 0 100% round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm)"];
        case "bottom":
          return ["inset(100% 0 0 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
        case "top":
          return ["inset(0 0 100% 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
      }
    }

    _setInitialPosition() {
      this.style.left = this.openFrom === "left" ? "0px" : null;
      this.style.right = this.style.left === "" ? "0px" : null;
      this.style.bottom = this.openFrom === "bottom" ? "0px" : null;
      this.style.top = this.style.bottom === "" ? "0px" : null;
    }

    _showTransition(animate11 = true) {
      let animationControls;
      this._setInitialPosition();

      if (reduceDrawerAnimation) {
        animationControls = Motion.animate(this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.2 });
      } else {
        let content = this.getShadowPartByName("content");
        let closeButton = this.getShadowPartByName("outside-close-button");

        animationControls = Motion.timeline([
          [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
          [content, { clipPath: this._getClipPathProperties() }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
          [content.children, { opacity: [0, 1] }, { duration: 0.15 }],
          [closeButton, { opacity: [0, 1] }, { at: "<", duration: 0.15 }]
        ]);
      }

      animate11 ? animationControls.play() : animationControls.finish();
      return animationControls.finished.then(() => this.classList.add("show-close-cursor"));
    }

    _hideTransition() {
      let animationControls;

      if (reduceDrawerAnimation) {
        animationControls = Motion.animate(this, { opacity: [1, 0], visibility: ["visibility", "hidden"] }, { duration: 0.2 });
      } else {
        let content = this.getShadowPartByName("content");
        let closeButton = this.getShadowPartByName("outside-close-button");

        animationControls = Motion.timeline([
          [closeButton, { opacity: [null, 0] }, { duration: 0.15 }],
          [content.children, { opacity: [null, 0] }, { at: "<", duration: 0.15 }],
          [content, { clipPath: this._getClipPathProperties().reverse() }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
          [this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }]
        ]);
      }
      return animationControls.finished.then(() => this.classList.remove("show-close-cursor"));
    }
  };

  if (!window.customElements.get("x-drawer")) {
    window.customElements.define("x-drawer", Drawer);
  }

  /**
   * The Popover and Listbox elements work together. These power a less intrusive dialog element. We use this for sort by and variant selectors.
   */
  var Popover = class extends DialogElement {
    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback();

      this.controls.forEach((control) => control.setAttribute("aria-haspopup", "dialog"));

      if (this.hasAttribute("close-on-listbox-select")) {
        this.addEventListener("listbox:select", this.hide, { signal: this._abortController.signal });
      }
    }

    get shadowDomTemplate() {
      return this.getAttribute("template") || "popover-default-template";
    }

    get shouldLock() {
      return window.matchMedia("screen and (max-width: 999px)").matches;
    }

    get shouldAppendToBody() {
      return window.matchMedia("screen and (max-width: 999px)").matches;
    }

    get anchor() {
      return {
        vertical: this.getAttribute("anchor-vertical") || "start",
        horizontal: this.getAttribute("anchor-horizontal") || "end"
      };
    }

    _showTransition(animate11 = true) {
      let animationControls; 
      let content = this.getShadowPartByName("content");
      let closeButton = this.getShadowPartByName("outside-close-button");

      this.style.display = "block";

      if (window.matchMedia("screen and (max-width: 999px)").matches) {
        this.style.left = "0px";
        this.style.right = null;
        this.style.bottom = "0px";
        this.style.top = null;
        animationControls = Motion.timeline([
          [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
          [content, { clipPath: ["inset(100% 0 0 0 round 8px)", "inset(0 0 0 0 round 8px"] }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
          [content.children, { opacity: [0, 1] }, { duration: 0.15 }],
          [closeButton, { opacity: [0, 1] }, { at: "<", duration: 0.15 }]
        ]);
      } else {
        let spacingBlockValue = "var(--popover-anchor-block-spacing)", spacingInlineValue = "var(--popover-anchor-inline-spacing)";
        this.style.left = this.anchor.horizontal === "start" ? spacingInlineValue : null;
        this.style.right = this.anchor.horizontal === "end" ? spacingInlineValue : null;

        if (this.anchor.vertical === "center") {
          this.style.top = `calc(50% - ${parseInt(this.clientHeight / 2)}px)`;
          this.style.bottom = null;
        } else {
          this.style.top = this.anchor.vertical === "end" ? `calc(100% + ${spacingBlockValue})` : null;
          this.style.bottom = this.anchor.vertical === "start" ? `calc(100% + ${spacingBlockValue})` : null;
        }

        animationControls = Motion.timeline([
          [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
          [content, { clipPath: "none" }, { at: "<", duration: 0 }],
          [content.children, { opacity: 1 }, { at: "<", duration: 0 }]
        ]);
      }

      animate11 ? animationControls.play() : animationControls.finish();
      return animationControls.finished;
    }

    _hideTransition() {
      let animationControls;
      if (window.matchMedia("screen and (max-width: 999px)").matches) {
        let content = this.getShadowPartByName("content");
        let closeButton = this.getShadowPartByName("outside-close-button");

        animationControls = Motion.timeline([
          [closeButton, { opacity: [null, 0] }, { duration: 0.15 }],
          [content.children, { opacity: [null, 0] }, { at: "<", duration: 0.15 }],
          [content, { clipPath: [null, "inset(100% 0 0 0 round 8px)"] }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
          [this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }]
        ]);
      } else {
        animationControls = Motion.timeline(this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 });
      }

      return animationControls.finished.then(() => this.style.display = "none");
    }
  };

  if (!window.customElements.get("x-popover")) {
    window.customElements.define("x-popover", Popover);
  }

  var Listbox = class extends HTMLElement {
    constructor() {
      super();
    }

    static get observedAttributes() {
      return ["aria-activedescendant"];
    }

    connectedCallback() {
      this._accessibilityInitialized = false;
      this._delegate = new Delegate();
      this._hiddenInput = this.querySelector('input[type="hidden"]');
      this.addEventListener("keydown", this._onKeyDown.bind(this));

      if (!this._accessibilityInitialized) {
        this.setAttribute("role", "listbox");
        this._hiddenInput?.addEventListener("change", this._onInputChanged.bind(this));
        Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
          option.addEventListener("click", this._onOptionClicked.bind(this));
          option.id = "option-" + (crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4));

          if (option.getAttribute("aria-selected") === "true") {
            this.setAttribute("aria-activedescendant", option.id);
          }
        });

        this._accessibilityInitialized = true;
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "aria-activedescendant" && oldValue !== null && newValue !== oldValue) {
        Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
          if (option.id === newValue) {
            option.setAttribute("aria-selected", "true");

            if (this._hiddenInput && this._hiddenInput.value !== option.value) {
              this._hiddenInput = option.value;
              this._hiddenInput.dispatchEvent(new Event("change"), { bubbles: true });
            }

            if (this.hasAttribute("aria-owns")) {
              this.getAttribute("aria-owns").split(" ").forEach((boundId) => {
                document.getElementById(boundId).textContent = option.getAttribute("title") || option.innerText || option.value;
              });
            }

            option.dispatchEvent(new CustomEvent("listbox:change", {
              bubbles: true,
              detail: {
                value: option.value
              }
            }));
          } else {
            option.setAttribute("aria-selected", "false");
          }
        });
      }
    }

    _onOptionClicked(event) {
      this.setAttribute("aria-activedescendant", event.currentTarget.id);
      event.currentTarget.dispatchEvent(new CustomEvent("listbox:select", {
        bubbles: true,
        detail: {
          value: event.currentTarget.value
        }
      }));
    }

    _onInputChanged(event) {
      this.setAttribute("aria-activedescendant", this.querySelector(`[role="option"][value="${CSS.escape(event.target.value)}"]`).id);
    }

    _onKeyDown(event) {
      if (event.key === "ArrowUp") {
        event.target.previousElementSibling?.focus();
        event.preventDefault();
      } else if (event.key === "ArrowDown") {
        event.target.nextElementSibling?.focus();
        event.preventDefault();
      }
    }
  }

  if (!window.customElements.get("x-listbox")) {
    window.customElements.define("x-listbox", Listbox);
  }


  /**
   * Mobile menu drawer. This has some complex functionality opening various panels.
   */
  var NavigationDrawer = class extends Drawer {
    constructor() {
      super();
      const delegate = new Delegate(this);
      delegate.on("click", "button[data-panel]", this._onPanelButtonClick.bind(this));
      this._isTransitioning = false;
      this.addEventListener("dialog:after-hide", () => {
        this.reinitializeDrawer();
      });
    }

    connectedCallback() {
      super.connectedCallback();
    }

    switchToPanel(panelIndex, linkListIndex = null) {
      const panels = this.querySelectorAll(".panel");
      let panelToHideTransform; 
      let panelToShowTransform; 
      let panelToHide = linkListIndex !== null ? panels[parseInt(panelIndex) - 1] : panels[parseInt(panelIndex) + 1];
      let panelToShow = panels[panelIndex]; 
      let linkLists = panelToShow.querySelectorAll(".panel__wrapper");
      let timelineSequence = [];

      panelToHideTransform = linkListIndex !== null ? ["translateX(0%)", "translateX(-100%)"] : ["translateX(0%)", "translateX(100%)"];
      panelToShowTransform = linkListIndex !== null ? ["translateX(100%)", "translateX(0%)"] : ["translateX(-100%)", "translateX(0%)"];

      timelineSequence.push(
        [panelToHide, { transform: panelToHideTransform, opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.3, opacity: { at: "+0.3" }, transform: { at: "+0.3" } }],
        "panelHidden",
        [panelToShow, { opacity: [0, 1], visibility: ["hidden", "visible"], transform: panelToShowTransform }, { at: "<", transform: { duration: 0.3 } }]
      );

      if (linkListIndex !== null) {
        timelineSequence.push(this.switchLinkList(linkLists, linkListIndex));
      }
      Motion.timeline(timelineSequence);
    }

    showPanel(panelIndex, linkListIndex = null) {
      const panels = this.querySelectorAll(".panel");

      let timelineSequence = [];
      let panelToShow = panels[panelIndex]; 
      let linkLists = panelToShow.querySelectorAll(".panel__wrapper");

      if (!panelToShow.hasAttribute("open") && !this._isTransitioning) {
        this._isTransitioning = true;
        timelineSequence.push(
          [this, { width: [this.offsetWidth + "px", (this.offsetWidth - parseInt(window.getComputedStyle(this).getPropertyValue("padding"))) * 2 + "px"] }, { duration: 0.2 }],
          [panelToShow, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { at: "<" }]
        );

        timelineSequence.push(this.switchLinkList(linkLists, linkListIndex));
      }

      if (this.previousLinkIndex && this.previousLinkIndex !== linkListIndex) {
        timelineSequence.push([linkLists[this.previousLinkIndex], { opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.2 }]);
      }

      Motion.timeline(timelineSequence).finished.then(() => {
        if (this.previousLinkIndex && this.previousLinkIndex !== linkListIndex) {
          linkLists[this.previousLinkIndex].removeAttribute("style");
        }

        if (panelToShow.hasAttribute("open") && this.previousLinkIndex !== linkListIndex) {
          Motion.timeline([this.switchLinkList(linkLists, linkListIndex)]);
        }

        this.previousLinkIndex = linkListIndex;
        panelToShow.setAttribute("open", "");
        this._isTransitioning = false;
      });
    }

    switchLinkList(linkLists, linkListIndex) {
      Array.from(linkLists).forEach((item) => {
        item.setAttribute("hidden", "");
      });

      linkLists[linkListIndex].removeAttribute("hidden");

      return [linkLists[linkListIndex].querySelectorAll("li"), { opacity: [0, 1], visibility: ["hidden", "visible"] }, { easing: "ease", duration: 0.2, at: "-0.15", delay: 0.025 }];
    }

    // Reset navigation drawer to initial state
    reinitializeDrawer() {
      const firstPanel = this.querySelector(".panel:first-child");
      firstPanel.style.opacity = "1";
      firstPanel.style.visibility = "visible";
      firstPanel.style.transform = "translateX(0%)";

      Array.from(this.querySelectorAll(".panel:not(:first-child)")).forEach((item) => {
        item.style.opacity = "0";
        item.style.visibility = "hidden";
        Array.from(item.querySelectorAll(".panel__wrapper")).forEach((list) => list.setAttribute("hidden", ""));
      });
    }

    setExpanded(target) {
      Array.from(this.querySelectorAll("button[data-panel]")).forEach((item) => {
        item.setAttribute("aria-expanded", "false");
      });

      if (target) {
        target.setAttribute("aria-expanded", "true");
      }
    }

    _onPanelButtonClick(event, target) {
      this.switchToPanel(...target.getAttribute("data-panel").split("-"));
    }
  }

  if (!window.customElements.get("navigation-drawer")) {
    window.customElements.define("navigation-drawer", NavigationDrawer);
  }



  /*------------------------------------*\
    #SEARCH
  \*------------------------------------*/

  var PredictiveSearch = class extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this.shadowRoot.appendChild(document.createRange().createContextualFragment(`<slot name="idle"></slot>`));
    }
  
    connectedCallback() {
      this._searchForm = this.closest("form");
      this._queryInput = this._searchForm.elements["q"];
      this._searchForm.addEventListener("submit", this._onFormSubmitted.bind(this));
      this._searchForm.addEventListener("reset", this._onSearchCleared.bind(this));
      this._queryInput.addEventListener("input", debounce(this._onInputChanged.bind(this), this.autoCompleteDelay));
    }
  
    /**
     * Return the delay in ms before we send the autocomplete request. Using a value too low can cause the results to
     * refresh too often, so we recommend to keep the default one
     */
    get autoCompleteDelay() {
      return 280;
    }

    /**
     * On the Support and Help centre blogs, we need an internal search. Shopify doesn't allow for searching within a specific blog, so we have to search via tag e.g. `tag:blog_Support` or `tag:blog_Help`.
     */
    get limitSearchTag() {
      return this.getAttribute("limit-search-tag");
    }
  
    /**
     * Check if the store supports the predictive API (some languages do not). When not supported, the predictive
     * search is simply disabled and only the standard search is used
     */
    supportsPredictiveApi() {
      return JSON.parse(document.getElementById("shopify-features").innerHTML)["predictiveSearch"];
    }
  
    /**
     * Check if the input is not empty, and if so, trigger the predictive search
     */
    _onInputChanged() {
      if (this._queryInput.value === "") {
        return this._onSearchCleared();
      }
  
      this._abortController?.abort();
      this._abortController = new AbortController();
  
      try {
        return this.supportsPredictiveApi() ? this._doPredictiveSearch() : this._doFallbackSearch();
      } catch (e) {
        if (e.name !== "AbortError") {
          throw e;
        }
      }
    }
  
    /**
     * Prevent the form submission if the query is empty
     */
    _onFormSubmitted(event) {
      if (this._queryInput.value === "" || this.limitSearchTag) {
        return event.preventDefault();
      }
    }
  
    /**
     * Do the actual predictive search
     */
    async _doPredictiveSearch() {
      await this._transitionToSlot("loading");

      let queryValue = this._queryInput.value;

      if (this.limitSearchTag) {
        queryValue = `${this.limitSearchTag}` + " " + `${this._queryInput.value}`;
      }

      const queryParams = `q=${encodeURIComponent(queryValue)}&section_id=${this.getAttribute("section-id")}&resources[limit]=10&resources[limit_scope]=each`;
  
      const nodeElement = new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}search/suggest?${queryParams}`, { signal: this._abortController.signal })).text(), "text/html");
  
      this.querySelector('[slot="results"]').replaceWith(document.importNode(nodeElement.querySelector('[slot="results"]'), true));
  
      return this._transitionToSlot("results");
    }
  
    /**
     * For merchants using store that do not support the predictive search, we have to fallback to standard search. Unfortunately,
     * the standard search is less convenient, and we have to simulate one request for each desired resource type
     */
    async _doFallbackSearch() {
      await this._transitionToSlot("loading");
      
      let queryValue = this._queryInput.value;

      if (this.limitSearchTag) {
        queryValue = `${this.limitSearchTag}` + " " + `${this._queryInput.value}`;
      }

      const queryParams = `q=${queryValue}&section_id=${this.getAttribute("section-id")}&resources[limit]=10&resources[limit_scope]=each`;
      const nodeElement = new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}search?${queryParams}`, { signal: this._abortController.signal })).text(), "text/html");
      this.querySelector('[slot="results"]').replaceWith(document.importNode(nodeElement.querySelector('[slot="results"]'), true));
      return this._transitionToSlot("results");
    }
  
    /**
     * If any search is pending, we abort them, and transition to the idle slot
     */
    _onSearchCleared() {
      this._abortController?.abort();
      this._queryInput.focus();
      return this._transitionToSlot("idle");
    }
  
    /**
     * Transition between different slot. To prevent useless animation, the slot does not transition if we go to the same slot
     */
    async _transitionToSlot(toSlotName) {
      if (this.shadowRoot.firstElementChild.name === toSlotName) {
        return;
      }
  
      let assignedNodes = this.shadowRoot.firstElementChild.assignedNodes();
  
      if (assignedNodes.length === 0) {
        this.shadowRoot.firstElementChild.setAttribute("name", toSlotName);
        return;
      } else {
        await Motion.animate(this.shadowRoot.firstElementChild.assignedNodes(), { opacity: [1, 0] }, { duration: 0.1 }).finished;
        this.shadowRoot.firstElementChild.setAttribute("name", toSlotName);
        return Motion.animate(this.shadowRoot.firstElementChild.assignedNodes(), { opacity: [0, 1], transform: ["translateY(5px)", "translateY(0)"] }, { duration: 0.1 }).finished;
      }
    }
  };
  
  if (!window.customElements.get("predictive-search")) {
    window.customElements.define("predictive-search", PredictiveSearch);
  }
  

  var SearchDrawer = class extends Drawer {
    get shouldAppendToBody() {
      return false;
    }
  };

  if (!window.customElements.get("search-drawer")) {
    window.customElements.define("search-drawer", SearchDrawer);
  }

  var MainSearch = class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      document.addEventListener("search:switch", this.searchSwitch.bind(this));
      this.searchForm = this.querySelector(".main-search__form");
      this.searchInput = this.searchForm.querySelector('input[name="q"]');
      this.sectionId = this.getAttribute("section-id");
      this.shopifySection = this.closest(".shopify-section");
    }

    async searchSwitch(event) {
      let url;
      this.shopifySection.classList.add("is-loading");
      let queryValue;

      if (event.detail.resource == "article") {
        queryValue = this.searchInput.value;
        url = `q=${queryValue}&type=article`;
      } else {
        queryValue = this.searchInput.value;
        url = `q=${queryValue}&type=product`;
      }

      window.location = `${window.Shopify.routes.root}search?${url}`;
    }
  }

  if (!window.customElements.get("main-search")) {
    window.customElements.define("main-search", MainSearch);
  }




  /*------------------------------------*\
    #CART
  \*------------------------------------*/

  var createCartPromise = () => {
    return new Promise(async (resolve) => {
      resolve(await (await fetch(`${Shopify.routes.root}cart.js`)).json());
    });
  };

  var fetchCart = createCartPromise();
  document.addEventListener("cart:change", (event) => {
    fetchCart = event.detail["cart"];
  });

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      fetchCart = createCartPromise();
    }
  });
  
  var CartCount = class extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment("<span><slot></slot></span>"));
    }

    connectedCallback() {
      this._abortController = new AbortController();
      document.addEventListener("cart:change", (event) => this.itemCount = event.detail["cart"]["item_count"], { signal: this._abortController.signal });

      document.addEventListener("cart:refresh", this._updateFromServer.bind(this), { signal: this._abortController.signal });
      window.addEventListener("pageshow", this._updateFromServer.bind(this));
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    async _updateFromServer() {
      this.itemCount = (await fetchCart)["item_count"];
    }

    get itemCount() {
      return parseInt(this.innerText);
    }

    set itemCount(count) {
      if (this.itemCount === count) {
        return;
      }

      if (count === 0) {
        Motion.animate(this, { opacity: 0 }, { duration: 0.1 });
        this.innerText = count;
      } else if (this.itemCount === 0) {
        Motion.animate(this, { opacity: 1 }, { duration: 0.1 });
        this.innerText = count;
      } else {
        (async () => {
          await Motion.animate(this.shadowRoot.firstElementChild, { transform: ["translateY(-50%)"], opacity: 0 }, { duration: 0.25, easing: [1, 0, 0, 1] }).finished;
          this.innerText = count;
          Motion.animate(this.shadowRoot.firstElementChild, { transform: ["translateY(50%)", "translateY(0)"], opacity: 1 }, { duration: 0.25, easing: [1, 0, 0, 1] });
        })();
      }
    }
  };
  
  if (!window.customElements.get("cart-count")) {
    window.customElements.define("cart-count", CartCount);
  }

  var CartDrawer = class extends Drawer {
    constructor() {
      super();
      this._onPrepareBundledSectionsListener = this._onPrepareBundledSections.bind(this);
      this._onCartChangedListener = this._onCartChanged.bind(this);
      this._onCartRefreshListener = this._onCartRefresh.bind(this);
      this._onVariantAddedListener = this._onVariantAdded.bind(this);
      window.addEventListener("pageshow", this._onPageShow.bind(this));
    }

    connectedCallback() {
      super.connectedCallback();
      document.addEventListener("cart:prepare-bundled-sections", this._onPrepareBundledSectionsListener);
      document.addEventListener("cart:change", this._onCartChangedListener);
      document.addEventListener("cart:refresh", this._onCartRefreshListener);
      document.addEventListener("variant:add", this._onVariantAddedListener);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener("cart:prepare-bundled-sections", this._onPrepareBundledSectionsListener);
      document.removeEventListener("cart:change", this._onCartChangedListener);
      document.removeEventListener("cart:refresh", this._onCartRefreshListener);
      document.removeEventListener("variant:add", this._onVariantAddedListener);
    }

    get shouldAppendToBody() {
      return false;
    }

    _onPrepareBundledSections(event) {
      event.detail.sections.push(extractSectionId(this));
    }

    /**
     * Update the cart drawer content when cart content changes.
     */
    async _onCartChanged(event) {
      const updatedDrawerContent = new DOMParser().parseFromString(event.detail.cart["sections"][extractSectionId(this)], "text/html");

      if (event.detail.cart["item_count"] > 0) {
        const currentInner = this.querySelector(".cart-drawer__inner"); 
        const updatedInner = updatedDrawerContent.querySelector(".cart-drawer__inner");

        if (!currentInner) {
          this.replaceChildren(document.createRange().createContextualFragment(updatedDrawerContent.querySelector(".cart-drawer").innerHTML));
        } else {
          setTimeout(() => {
            currentInner.innerHTML = updatedInner.innerHTML;
          }, event.detail.baseEvent === "variant:add" ? 0 : 1250);

          this.querySelector('[slot="footer"]').replaceChildren(...updatedDrawerContent.querySelector('[slot="footer"]').childNodes);
        }
      } else {
        await Motion.animate(this.children, { opacity: 0 }, { duration: 0.15 }).finished;

        this.replaceChildren(...updatedDrawerContent.querySelector(".cart-drawer").childNodes);

        Motion.animate(this.querySelector(".empty-state"), { opacity: [0, 1], transform: ["translateY(20px)", "translateY(0)"] }, { duration: 0.15 });
      }
    }

    /**
     * Handle the case when the page is served from BF cache
     */
    _onPageShow(event) {
      if (!event.persisted) {
        return;
      }
      this._onCartRefresh();
    }

    /**
     * Listeners called when a new variant has been added
     */
    _onVariantAdded(event) {
      if (window.themeVariables.settings.cartType !== "drawer" || event.detail?.blockCartDrawerOpening) {
        return;
      }
      this.show();
    }

    /**
     * Force a complete refresh of the cart drawer (this is called by dispatching the 'cart:refresh' on the document)
     */
    async _onCartRefresh() {
      const tempDiv = document.createElement("div");

      tempDiv.innerHTML = await (await fetch(`${window.Shopify.routes.root}?section_id=${extractSectionId(this)}`)).text();

      this.replaceChildren(...tempDiv.querySelector("#cart-drawer").children);
    }
  };

  if (!window.customElements.get("cart-drawer")) {
    window.customElements.define("cart-drawer", CartDrawer);
  }

  var LineItem = class extends HTMLElement {
    connectedCallback() {
      this.pillLoaderElement = this.querySelector("pill-loader");
      this.addEventListener("line-item:will-change", this._onWillChange.bind(this));
      this.addEventListener("line-item:change", this._onChanged.bind(this));
    }

    _onWillChange() {
      this.pillLoaderElement.setAttribute("aria-busy", "true");
    }

    async _onChanged(event) {
      this.pillLoaderElement.removeAttribute("aria-busy");
      if (event.detail.cart["item_count"] === 0 || event.detail.quantity !== 0) {
        return;
      }

      let marginCompensation = 0;
      if (this.nextElementSibling) {
        marginCompensation = `-${getComputedStyle(this.nextElementSibling).paddingTop}`;
      }

      await Motion.animate(this, { height: [`${this.clientHeight}px`, 0], marginBottom: [0, marginCompensation], overflow: "hidden", opacity: [1, 0] }, { duration: 0.2, easing: "ease" }).finished;
      this.remove();
    }
  };

  if (!window.customElements.get("line-item")) {
    window.customElements.define("line-item", LineItem);
  }

  var LineItemQuantity = class extends HTMLElement {
    constructor() {
      super();
      this.delegate = new Delegate(this);
      this.delegate.on("change", "[data-line-key]", this._onQuantityChanged.bind(this));
      this.delegate.on("click", '[href*="/cart/change"]', this._onChangeLinkClicked.bind(this));
    }

    _onQuantityChanged(event, target) {
      if (parseInt(target.value) > parseInt(target.getAttribute("max"))) {
        target.value = parseInt(target.getAttribute("value"));
        alert(window.themeVariables.strings.cartQuantityCannotAddMore);
        return;
      } else {
        this._changeLineItemQuantity(target.getAttribute("data-line-key"), parseInt(target.value));
      }
    }

    _onChangeLinkClicked(event, target) {
      event.preventDefault();
      const url = new URL(target.href);
      this._changeLineItemQuantity(url.searchParams.get("id"), parseInt(url.searchParams.get("quantity")));
    }

    async _changeLineItemQuantity(lineKey, targetQuantity) {
      if (window.themeVariables.settings.pageType === "cart") {
        window.location.href = `${Shopify.routes.root}cart/change?id=${lineKey}&quantity=${targetQuantity}`;
      } else {
        const lineItem = this.closest("line-item");

        lineItem?.dispatchEvent(new CustomEvent("line-item:will-change", { bubbles: true, detail: { targetQuantity } }));

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

        const cartContent = await (await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: targetQuantity,
            sections: sectionsToBundle
          })
        })).json();

        const updatedLineItem = cartContent["items"].find(item => item["key"] === lineKey);

        lineItem?.dispatchEvent(new CustomEvent("line-item:change", {
          bubbles: true,
          detail: {
            quantity: updatedLineItem ? updatedLineItem["quantity"] : 0,
            cart: cartContent
          }
        }));

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "line-item:change",
            cart: cartContent
          }
        }));
      }
    }
  };

  if (!window.customElements.get("line-item-quantity")) {
    window.customElements.define("line-item-quantity", LineItemQuantity);
  }

  var ShippingEstimator = class extends HTMLElement {
    constructor() {
      super();
      this._estimateShippingListener = this._estimateShipping.bind(this);
    }

    connectedCallback() {
      this.submitButton = this.querySelector('[type="submit"]');
      this.resultsElement = this.lastElementChild;
      this.submitButton.addEventListener("click", this._estimateShippingListener);
    }

    disconnectedCallback() {
      this.submitButton.removeEventListener("click", this._estimateShippingListener);
    }

    /**
     * @doc https://shopify.dev/docs/themes/ajax-api/reference/cart#generate-shipping-rates
     */
    async _estimateShipping(event) {
      event.preventDefault();
      const zip = this.querySelector('[name="address[zip]"]').value; 
      const country = this.querySelector('[name="address[country]"]').value; const province = this.querySelector('[name="address[province]"]').value;

      this.submitButton.setAttribute("aria-busy", "true");

      const prepareResponse = await fetch(`${Shopify.routes.root}cart/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`, { method: "POST" });

      if (prepareResponse.ok) {
        const shippingRates = await this._getAsyncShippingRates(zip, country, province);
        this._formatShippingRates(shippingRates);
      } else {
        const jsonError = await prepareResponse.json();
        this._formatError(jsonError);
      }

      this.resultsElement.hidden = false;
      this.submitButton.removeAttribute("aria-busy");
    }

    async _getAsyncShippingRates(zip, country, province) {
      const response = await fetch(`${Shopify.routes.root}cart/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`);
      const responseAsText = await response.text();

      if (responseAsText === "null") {
        return this._getAsyncShippingRates(zip, country, province);
      } else {
        return JSON.parse(responseAsText)["shipping_rates"];
      }
    }

    _formatShippingRates(shippingRates) {
      let formattedShippingRates = shippingRates.map((shippingRate) => {
        return `<li>${shippingRate["presentment_name"]}: ${shippingRate["currency"]} ${shippingRate["price"]}</li>`;
      });

      this.resultsElement.innerHTML = `
        <div class="v-stack gap-2">
          <p>${shippingRates.length === 0 ? window.themeVariables.strings.shippingEstimatorNoResults : shippingRates.length === 1 ? window.themeVariables.strings.shippingEstimatorOneResult : window.themeVariables.strings.shippingEstimatorMultipleResults}</p>
          ${formattedShippingRates === "" ? "" : `<ul class="list-disc" role="list">${formattedShippingRates.join("")}</ul>`}
        </div>
      `;
    }

    _formatError(errors) {
      let formattedShippingRates = Object.keys(errors).map((errorKey) => {
        return `<li>${errors[errorKey]}</li>`;
      });
      this.resultsElement.innerHTML = `
        <div class="v-stack gap-2">
          <p>${window.themeVariables.strings.shippingEstimatorError}</p>
          <ul class="list-disc" role="list">${formattedShippingRates}</ul>
        </div>
      `;
    }
  };

  if (!window.customElements.get("shipping-estimator")) {
    window.customElements.define("shipping-estimator", ShippingEstimator);
  }
  
  var CartNote = class extends HTMLElement {
    constructor() {
      super();
      this.addEventListener("change", this._onNoteChanged);
    }

    _onNoteChanged(event) {
      if (event.target.getAttribute("name") !== "note") {
        return;
      }

      fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: event.target.value }),
        keepalive: true
        // Allows to make sure the request is fired even when submitting the form
      });
    }
  };

  var FreeShippingBar = class extends HTMLElement {
    static get observedAttributes() {
      return ["threshold", "total-price"];
    }

    constructor() {
      super();
      this._onCartChangedListener = this._onCartChanged.bind(this);
    }

    async connectedCallback() {
      this.threshold = Math.round(this.threshold * (Shopify.currency.rate || 1));
      document.addEventListener("cart:change", this._onCartChangedListener);
    }

    disconnectedCallback() {
      document.removeEventListener("cart:change", this._onCartChangedListener);
    }

    get threshold() {
      return parseFloat(this.getAttribute("threshold"));
    }

    set threshold(value) {
      this.setAttribute("threshold", value);
    }

    get totalPrice() {
      return parseFloat(this.getAttribute("total-price"));
    }

    set totalPrice(value) {
      this.setAttribute("total-price", value);
    }

    async attributeChangedCallback(attribute, oldValue, newValue) {
      await window.customElements.whenDefined("progress-bar");
      const progressBarElement = this.querySelector("progress-bar");
      switch (attribute) {
        case "threshold":
          progressBarElement.valueMax = newValue;
          break;
        case "total-price":
          progressBarElement.valueNow = newValue;
          break;
      }
      this._updateMessage();
    }

    _updateMessage() {
      const messageElement = this.querySelector("span");
      if (this.totalPrice >= this.threshold) {
        messageElement.innerHTML = this.getAttribute("reached-message");
      } else {
        const replacement = `<span class="bold text-accent">${formatMoney(this.threshold - this.totalPrice).replace(/\$/g, "$$$$")}</span>`;
        messageElement.innerHTML = this.getAttribute("unreached-message").replace(new RegExp("({{.*}})", "g"), replacement);
      }
    }

    _onCartChanged(event) {
      const priceForItems = event.detail["cart"]["items"].filter((item) => item["requires_shipping"]).reduce((sum, item) => sum + item["final_line_price"], 0); 
      const cartDiscount = event.detail["cart"]["cart_level_discount_applications"].reduce((sum, discountAllocation) => sum + discountAllocation["total_allocated_amount"], 0);
      this.totalPrice = priceForItems - cartDiscount;
    }
  };

  if (!window.customElements.get("free-shipping-bar")) {
    window.customElements.define("free-shipping-bar", FreeShippingBar);
  }

  var FreeShippingMessage = class extends FreeShippingBar {
    constructor() {
      super();
    }

    get threshold() {
      return parseFloat(this.getAttribute("threshold"));
    }

    set threshold(value) {
      this.setAttribute("threshold", value);
    }

    get totalPrice() {
      return parseFloat(this.getAttribute("total-price"));
    }

    set totalPrice(value) {
      this.setAttribute("total-price", value);
    }

    async attributeChangedCallback(attribute, oldValue, newValue) {
      this._updateMessage();
    }
  };

  if (!window.customElements.get("free-shipping-message")) {
    window.customElements.define("free-shipping-message", FreeShippingMessage);
  }

  var ProgressBar = class extends HTMLElement {
    static get observedAttributes() {
      return ["aria-valuenow", "aria-valuemax"];
    }

    set valueMax(value) {
      this.setAttribute("aria-valuemax", value);
    }

    set valueNow(value) {
      this.setAttribute("aria-valuenow", value);
    }

    attributeChangedCallback() {
      this.style.setProperty("--progress", `${Math.min(1, this.getAttribute("aria-valuenow") / this.getAttribute("aria-valuemax"))}`);
    }
  };

  if (!window.customElements.get("progress-bar")) {
    window.customElements.define("progress-bar", ProgressBar);
  }

  var PillLoader = class extends HTMLElement {
    static get observedAttributes() {
      return ["aria-busy"];
    }

    connectedCallback() {
      this.innerHTML = `
        <div class="loader-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
        
        <svg class="loader-checkmark" fill="none" width="9" height="8" viewBox="0 0 9 8">
          <path d="M1 3.5 3.3 6 8 1" stroke="currentColor" stroke-width="2"/>
        </svg>
      `;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (newValue === "true") {
        Motion.timeline([
          [this, { opacity: [0, 1], visibility: "visible", transform: ["translateY(5px)", "translateY(0)"] }, { duration: 0.15 }],
          [this.firstElementChild, { opacity: 1, transform: ["translateY(0)"] }, { duration: 0.15, at: "<" }],
          [this.lastElementChild, { opacity: 0 }, { duration: 0, at: "<" }]
        ]);

        Motion.animate(this.firstElementChild.querySelectorAll("span"), { opacity: [1, 0.1] }, { duration: 0.35, delay: Motion.stagger(0.35 / 3), direction: "alternate", repeat: Infinity });
      } else {
        Motion.timeline([
          [this.firstElementChild, { opacity: 0, transform: ["translateY(0)", "translateY(-2px)"] }, { duration: 0.15 }],
          [this.lastElementChild, { opacity: 1, transform: ["translateY(2px)", "translateY(0)"] }, { duration: 0.15 }],
          [this, { opacity: 0, transform: ["translateY(0)", "translateY(-5px)"], visibility: "hidden" }, { duration: 0.15, at: "+0.8" }]
        ]);
      }
    }
  };

  if (!window.customElements.get("pill-loader")) {
    window.customElements.define("pill-loader", PillLoader);
  }






  /*------------------------------------*\
    #FACETS
  \*------------------------------------*/

  var FacetApplyButton = class extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener("click", this._closeDrawer);
      this.form.addEventListener("change", this._updateCount.bind(this));
      this.filterCountElement = document.createElement("span");
      this.appendChild(this.filterCountElement);
    }

    connectedCallback() {
      this._updateCount();
    }

    _updateCount() {
      const form = new FormData(this.form);
      const count = Array.from(form.entries()).filter(([key, value]) => key !== "sort_by" && value !== "").length;
      this.filterCountElement.innerText = ` (${count})`;
    }

    async _closeDrawer() {
      this.closest("facet-drawer").hide();
    }
  };

  if (!window.customElements.get("facet-apply-button")) {
    window.customElements.define("facet-apply-button", FacetApplyButton, { extends: "button" });
  }


  var FacetDrawer = class extends Drawer {
    constructor() {
      super();
      this.addEventListener("dialog:after-hide", this._submitForm);
    }

    _submitForm() {
      const form = this.querySelector("#facet-form");
      if (HTMLFormElement.prototype.requestSubmit) {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event("submit", { cancelable: true }));
      }
    }
  };

  if (!window.customElements.get("facet-drawer")) {
    window.customElements.define("facet-drawer", FacetDrawer);
  }

  var abortController = null;
  var openElements = new Set();

  document.addEventListener("facet:update", async (event) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    const url = event.detail.url;
    const shopifySection = document.getElementById(`shopify-section-${event.detail.sectionId}`);
    const updateHistory = event.detail.updateHistory !== false; // Default to true if not provided

    shopifySection.classList.add("is-loading");
    
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete("section_id");

    // Update history only if updateHistory is true and check if the new URL is different from the current one
    if (updateHistory && clonedUrl.toString() !== window.location.href) {
      history.pushState({ url: clonedUrl.toString() }, "", clonedUrl.toString());
    }

    try {
      url.searchParams.set("section_id", event.detail.sectionId);

      const tempContent = new DOMParser().parseFromString(await (await cachedFetch(url.toString(), { signal: abortController.signal })).text(), "text/html");

      Array.from(tempContent.querySelectorAll("details, facet-dialog")).forEach((item) => {
        if (openElements.has(item.id)) {
          item.setAttribute("open", "");
        }
      });

      shopifySection.replaceChildren(...document.importNode(tempContent.querySelector(".shopify-section"), true).childNodes);

      shopifySection.classList.remove("is-loading");

      var trustPilotWidgets = document.getElementsByClassName("trustpilot-widget");
      for(var i=0; i<trustPilotWidgets.length; i++) {
          window.Trustpilot.loadFromElement(trustPilotWidgets[i]);
      }

      const scrollToElement = shopifySection.querySelector(".collection__results");
      const scrollToBoundingRect = scrollToElement.getBoundingClientRect();

      if (scrollToBoundingRect.top < parseInt(getComputedStyle(scrollToElement).scrollPaddingTop || 0)) {
        scrollToElement.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    } catch (e) {
    }
  });

  /**
   * We use `popstate` to allow for triggering filter changes based on back/forward navigation.
   * `popstate` fires when the active history entry changes, but it doesn't fire for initial page load.
   * Therefore, we store the initial state (unfiltered collection page). This ensures if someone selects a filter and clicks back, they will return to the unfiltered collection.
   */
  window.addEventListener('DOMContentLoaded', (event) => {
    const initialUrl = window.location.href;
    history.replaceState({ url: initialUrl }, '', initialUrl);
  });
  
  /**
   * The `popstate` event will re-fetch data when the user navigates with back/forward buttons
   */
  window.addEventListener('popstate', (event) => {
    if (event.state && event.state.url) {
      document.dispatchEvent(new CustomEvent("facet:update", { 
        detail: { 
          url: new URL(event.state.url), 
          sectionId: document.querySelector("#facet-form").getAttribute("section-id")
        }
      }));
    }
  });
  
  

  var FacetForm = class extends HTMLFormElement {
    constructor() {
      super();

      // Check if the form has been changed since load. By default, an unchanged form is clean.
      this._isDirty = false;

      this.addEventListener("change", this._onFormChanged);
      this.addEventListener("submit", this._onFormSubmitted);
    }

    connectedCallback() {
      Array.from(this.querySelectorAll("details, facet-dialog")).forEach((disclosureElement) => {
        if (disclosureElement.open) {
          openElements.add(disclosureElement.id);
        }

        disclosureElement.addEventListener("toggle", () => {
          if (disclosureElement.open) {
            openElements.add(disclosureElement.id);
          } else {
            openElements.delete(disclosureElement.id);
          }
        });
      });
    }

    _buildUrl() {
      // 1. Gather all form data and create a URLSearchParams object.
      const searchParams = new URLSearchParams(new FormData(this));
    
      // 2. Create a new URL object based on the form's action attribute.
      const url = new URL(this.action);
    
      // 3. Clear any existing search parameters in the URL.
      url.search = "";
    
      // 4. Append each key-value pair from the searchParams to the URL.
      searchParams.forEach((value, key) => url.searchParams.append(key, value));
    
      // 5. Clear certain parameters if their value is an empty string.
      ["page", "filter.v.price.gte", "filter.v.price.lte"].forEach((optionToClear) => {
        if (url.searchParams.get(optionToClear) === "") {
          url.searchParams.delete(optionToClear);
        }
      });
    
      // 6. Add or update a "section_id" parameter in the URL.
      url.searchParams.set("section_id", this.getAttribute("section-id"));
    
      // 7. Return the final constructed URL.
      return url;
    }
    

    _onFormChanged() {
      this._isDirty = true;

      /**
       * Our facet form on desktop has this attribute which means clicking filters will trigger form submission automatically.
       * In contrast, the facet drawer will only trigger the submission once the drawer is closed.
       */
      if (this.hasAttribute("update-on-change")) {
        /**
         * requestSubmit is a modern method which will run a full form validation. In contrast, the older submit method doesn't.
         */
        if (HTMLFormElement.prototype.requestSubmit) {
          this.requestSubmit();
        } else {
          this.dispatchEvent(new Event("submit", { cancelable: true }));
        }
      } else {
        cachedFetch(this._buildUrl().toString());
      }
    }

    _onFormSubmitted(event) {
      event.preventDefault();

      if (!this._isDirty) {
        return;
      }

      this.dispatchEvent(new CustomEvent("facet:update", {
        bubbles: true,
        detail: {
          url: this._buildUrl(),
          sectionId: this.getAttribute("section-id")
        }
      }));

      this._isDirty = false;
    }
  };

  if (!window.customElements.get("facet-form")) {
    window.customElements.define("facet-form", FacetForm, { extends: "form" });
  }


  var FacetLink = class extends HTMLAnchorElement {
    constructor() {
      super();
      this.addEventListener("click", this._onFacetUpdate);
    }

    _onFacetUpdate(event) {
      event.preventDefault();

      const sectionId = event.target.closest(".shopify-section").id.replace("shopify-section-", ""); 
      const url = new URL(this.href);

      url.searchParams.set("section_id", sectionId);
      this.dispatchEvent(new CustomEvent("facet:update", {
        bubbles: true,
        detail: {
          url,
          sectionId: sectionId
        }
      }));
    }
  };

  if (!window.customElements.get("facet-link")) {
    window.customElements.define("facet-link", FacetLink, { extends: "a" });
  }


  var FacetSortBy = class extends HTMLElement {
    constructor() {
      super();
      this.addEventListener("listbox:change", this._onValueChanged);
    }

    _onValueChanged(event) {
      const url = new URL(location.href); 
      const sectionId = event.target.closest(".shopify-section").id.replace("shopify-section-", "");
      
      url.searchParams.set("sort_by", event.detail.value);
      url.searchParams.set("section_id", sectionId);
      url.searchParams.delete("page");

      this.dispatchEvent(new CustomEvent("facet:update", {
        bubbles: true,
        detail: {
          url,
          sectionId: sectionId
        }
      }));
    }
  };

  if (!window.customElements.get("facet-sort-by")) {
    window.customElements.define("facet-sort-by", FacetSortBy);
  }




  /*------------------------------------*\
    #PRODUCT FORM
  \*------------------------------------*/

  var ProductForm = class extends HTMLFormElement {
    constructor() {
      super();
      this.addEventListener("submit", this._onSubmit);
    }

    connectedCallback() {
      this.id.disabled = false;
    }

    async _onSubmit(event) {
      event.preventDefault();

      if (!this.checkValidity()) {
        this.reportValidity();
        return;
      }

      const submitButtons = Array.from(this.elements).filter((button) => button.type === "submit");

      submitButtons.forEach((submitButton) => {
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-busy", "true");
      });

      let sectionsToBundle = ["variant-added"];

      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

      const formData = new FormData(this);
      formData.set("sections", sectionsToBundle.join(","));
      formData.set("sections_url", `${Shopify.routes.root}variants/${this.id.value}`);

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
          // Needed for Shopify to check inventory
        }
      });

      submitButtons.forEach((submitButton) => {
        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-busy");
      });

      const responseJson = await response.json();

      if (response.ok) {
        if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
          return window.location.href = `${Shopify.routes.root}cart`;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent["sections"] = responseJson["sections"];

        this.dispatchEvent(new CustomEvent("variant:add", {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty("items") ? responseJson["items"] : [responseJson],
            cart: cartContent
          }
        }));

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartContent
          }
        }));
      } else {
        this.dispatchEvent(new CustomEvent("cart:error", {
          bubbles: true,
          detail: {
            error: responseJson["description"]
          }
        }));

        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    }
  };

  if (!window.customElements.get("product-form")) {
    window.customElements.define("product-form", ProductForm, { extends: "form" });
  }

  var BuyButtons = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
      this._onCartErrorListener = this._onCartError.bind(this);
    }

    connectedCallback() {
      this._productForm = document.forms[this.getAttribute("form")];
      this._productForm?.addEventListener("variant:change", this._onVariantChangedListener);
      this._productForm?.addEventListener("cart:error", this._onCartErrorListener);
    }

    disconnectedCallback() {
      this._productForm?.removeEventListener("variant:change", this._onVariantChangedListener);
      this._productForm?.removeEventListener("cart:error", this._onCartErrorListener);
    }

    _onVariantChanged(event) {
      const addToCartButton = this.querySelector('button[type="submit"]'); 
      const paymentButton = this.querySelector(".shopify-payment-button");
      addToCartButton.classList.remove("button--subdued");
      addToCartButton.disabled = !event.detail.variant || !event.detail.variant["available"];

      const addToCartButtonText = addToCartButton.getAttribute("is") === "custom-button" ? addToCartButton.firstElementChild : addToCartButton;

      if (!event.detail.variant) {
        addToCartButtonText.innerHTML = window.themeVariables.strings.unavailableButton;
        addToCartButton.classList.add("button--subdued");

        if (paymentButton) {
          paymentButton.style.display = "none";
        }
      } else {
        addToCartButton.classList.add(event.detail.variant["available"] ? paymentButton || this.hasAttribute("force-secondary-button") ? "button" : "button" : "button--subdued");

        addToCartButtonText.innerHTML = event.detail.variant["available"] ? window.themeVariables.strings.addToCartButton : window.themeVariables.strings.soldOutButton;

        if (paymentButton) {
          paymentButton.style.display = event.detail.variant["available"] ? "block" : "none";
        }
      }
    }

    _onCartError(event) {
      const errorBanner = document.createElement("div");
      errorBanner.classList.add("banner", "banner--error", "justify-center");
      errorBanner.setAttribute("role", "alert");
      errorBanner.style.gridColumn = "1/-1";
      errorBanner.style.marginBottom = "1rem";

      errorBanner.innerHTML = `
        <svg role="presentation" focusable="false" width="18" height="18" class="icon icon-error" style="--icon-height: 18px" viewBox="0 0 18 18">
          <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
          <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
        </svg>
        
        <p>${event.detail.error}</p>
      `;
      this.before(errorBanner);

      setTimeout(async () => {
        await errorBanner.animate({ opacity: [1, 0] }, { duration: 250, fill: "forwards" }).finished;
        errorBanner.remove();
      }, 5e3);
    }
  };

  if (!window.customElements.get("buy-buttons")) {
    window.customElements.define("buy-buttons", BuyButtons);
  }


  /*------------------------------------*\
    #PRODUCT PRICES
  \*------------------------------------*/

  var currencyFormat = window.themeVariables.settings.moneyFormat;

  var SalePrice = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;
      this.lastChild.replaceWith(document.createRange().createContextualFragment(formatMoney(variant["price"], currencyFormat)));
      this.classList.toggle("text-on-sale", variant["compare_at_price"] > variant["price"]);
    }
  };


  var CompareAtPrice = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;
      this.lastChild.replaceWith(document.createRange().createContextualFragment(formatMoney(variant["compare_at_price"], currencyFormat)));
      this.hidden = !(variant["compare_at_price"] > variant["price"]);
    }
  };


  var UnitPrice = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;
      if (!variant["unit_price"]) {
        return this.hidden = true;
      }
      const referenceValue = variant["unit_price_measurement"]["reference_value"] !== 1 ? variant["unit_price_measurement"]["reference_value"] : "";
      const node = document.createRange().createContextualFragment(
        `${formatMoney(variant["unit_price"])}/${referenceValue}${variant["unit_price_measurement"]["reference_unit"]}`
      );
      this.lastChild.replaceWith(node);
      this.hidden = false;
    }
  };

  if (!window.customElements.get("sale-price")) {
    window.customElements.define("sale-price", SalePrice);
  }

  if (!window.customElements.get("compare-at-price")) {
    window.customElements.define("compare-at-price", CompareAtPrice);
  }

  if (!window.customElements.get("unit-price")) {
    window.customElements.define("unit-price", UnitPrice);
  }


  /*------------------------------------*\
    #PRODUCT BADGES
  \*------------------------------------*/

  var SoldOutBadge = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      this.hidden = event.detail.variant["available"];
    }
  };

  var InStockBadge = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      this.hidden = !event.detail.variant["available"];
    }
  };

  var OnSaleBadge = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;

      if (!variant["available"]) {
        this.hidden = true;
        return;
      }

      if (variant["compare_at_price"] > variant["price"]) {
        this.hidden = false;
        if (this.hasAttribute("discount-mode")) {
          const savings = this.getAttribute("discount-mode") === "percentage" ? `${Math.round((variant["compare_at_price"] - variant["price"]) * 100 / variant["compare_at_price"])}%` : formatMoney(variant["compare_at_price"] - variant["price"]);
          this.innerHTML = `${window.themeVariables.strings.discountBadge.replace("@@", savings)}`;
        }
      } else {
        this.hidden = true;
      }
    }
  };

  if (!window.customElements.get("sold-out-badge")) {
    window.customElements.define("sold-out-badge", SoldOutBadge);
  }

  if (!window.customElements.get("in-stock-badge")) {
    window.customElements.define("in-stock-badge", InStockBadge);
  }

  if (!window.customElements.get("on-sale-badge")) {
    window.customElements.define("on-sale-badge", OnSaleBadge);
  }



  /*------------------------------------*\
    #PRODUCT VARIANTS
  \*------------------------------------*/

  var VariantPicker = class extends HTMLElement {
    async connectedCallback() {
      this._abortController = new AbortController();
      this.masterSelector = document.forms[this.getAttribute("form")].id;
      this.optionSelectors = Array.from(this.querySelectorAll("[data-option-selector]"));

      if (!this.masterSelector) {
        console.warn(`The variant selector for product with handle ${this.productHandle} is not linked to any product form.`);
        return;
      }

      this.product = await ProductLoader.load(this.productHandle);

      this.optionSelectors.forEach((optionSelector) => {
        optionSelector.addEventListener("change", this._onOptionChanged.bind(this), { signal: this._abortController.signal });
      });

      this.masterSelector.addEventListener("change", this._onMasterSelectorChanged.bind(this), { signal: this._abortController.signal });
      this._updateDisableSelectors();
      this.selectVariant(this.selectedVariant["id"]);
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get selectedVariant() {
      return this._getVariantById(parseInt(this.masterSelector.value));
    }

    get productHandle() {
      return this.getAttribute("handle");
    }

    get hideSoldOutVariants() {
      return this.hasAttribute("hide-sold-out-variants");
    }

    get updateUrl() {
      return this.hasAttribute("update-url");
    }

    /**
     * Select a new variant by its ID
     */
    selectVariant(id) {
      if (!this._isVariantSelectable(this._getVariantById(id))) {
        id = this._getFirstMatchingAvailableOrSelectableVariant()["id"];
      }

      const previousVariant = this.selectedVariant;

      if (previousVariant && previousVariant.id === id) {
        return;
      }

      this.masterSelector.value = id;
      this.masterSelector.dispatchEvent(new Event("change", { bubbles: true }));

      if (this.updateUrl && history.replaceState) {
        const newUrl = new URL(window.location.href);
        if (id) {
          newUrl.searchParams.set("variant", id);
        } else {
          newUrl.searchParams.delete("variant");
        }
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }

      this._updateDisableSelectors();

      this.masterSelector.form.dispatchEvent(new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          product: this.product,
          variant: this.selectedVariant,
          previousVariant
        }
      }));
    }

    _onOptionChanged(event) {
      if (!event.target.name.startsWith("option")) {
        return;
      }

      this.selectVariant(this._getVariantFromOptions()?.id);
    }

    _onMasterSelectorChanged() {
      const options = this.selectedVariant?.options || [];

      options.forEach((value, index) => {
        let input = this.optionSelectors[index].querySelector(`input[type="radio"][name="option${index + 1}"][value="${CSS.escape(value)}"], input[type="hidden"][name="option${index + 1}"], select[name="option${index + 1}"]`);
        let triggerChangeEvent = false;

        if (input.tagName === "SELECT" || input.tagName === "INPUT" && input.type === "hidden") {
          triggerChangeEvent = input.value !== value;
          input.value = value;
        } else if (input.tagName === "INPUT" && input.type === "radio") {
          triggerChangeEvent = !input.checked && input.value === value;
          input.checked = input.value === value;
        }

        if (triggerChangeEvent) {
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }

    /**
     * Get the product variant by its ID
     */
    _getVariantById(id) {
      return this.product["variants"].find((variant) => variant["id"] === id);
    }

    /**
     * Get the variant based on the options
     */
    _getVariantFromOptions() {
      const options = this._getSelectedOptionValues();
      return this.product["variants"].find((variant) => {
        return variant["options"].every((value, index) => value === options[index]);
      });
    }

    /**
     * Detect if a specific variant is selectable. This is used when the "hide sold out variant" option is enabled, to allow
     * returning true only if the variant is actually available
     */
    _isVariantSelectable(variant) {
      if (!variant) {
        return false;
      } else {
        return variant["available"] || !this.hideSoldOutVariants && !variant["available"];
      }
    }

    /**
     * This method is used internally to select an available or selectable variant, when the current choice does not
     * match the requirements. For instance, if sold out variants are configured to be hidden, but that the choices end
     * up being a non-valid variant, the theme automatically changes the variant to match the requirements. In the case
     * the customer end up on variant combinations that do not exist, it also switches to a valid combination.
     *
     * The algorithm is as follows: if we have for instance three options "Color", "Size" and "Material", we pop the last
     * option (Material) and try to find the first available variant for the given Color and Size. If none is found we
     * remove the second option (Size) and try to find the first available variant for the selected color. Finally, if none
     * is found we return the first available variant independently of any choice.
     */
    _getFirstMatchingAvailableOrSelectableVariant() {
      let options = this._getSelectedOptionValues(); 
      let matchedVariant = null; 
      let slicedCount = 0;

      do {
        options.pop();
        slicedCount += 1;
        matchedVariant = this.product["variants"].find((variant) => {
          if (this.hideSoldOutVariants) {
            return variant["available"] && variant["options"].slice(0, variant["options"].length - slicedCount).every((value, index) => value === options[index]);
          } else {
            return variant["options"].slice(0, variant["options"].length - slicedCount).every((value, index) => value === options[index]);
          }
        });
      } while (!matchedVariant && options.length > 0);
      return matchedVariant;
    }

    _getSelectedOptionValues() {
      return this.optionSelectors.map((optionSelector) => {
        return optionSelector.querySelector('input[name^="option"][type="hidden"], input[name^="option"]:checked, select[name^="option"]').value;
      });
    }

    /**
     * We add specific class to sold out variants based on the selectors
     */
    _updateDisableSelectors() {
      const selectedVariant = this.selectedVariant;

      if (!selectedVariant) {
        return;
      }

      this._updateDisableSelectorsForOptionLevel(0, selectedVariant);
    }

    _updateDisableSelectorsForOptionLevel(level, selectedVariant) {
      if (!this.optionSelectors[level]) {
        return;
      }

      const applyClassToSelector = (selector, valueIndex, available, hasAtLeastOneCombination) => {
        const optionValue = Array.from(selector.querySelectorAll("[data-option-value]"))[valueIndex];
        /* Uncomment the below in order to hide unavailable variant options */
        // optionValue.toggleAttribute("hidden", !hasAtLeastOneCombination);

        // This will disable unavailable variant options
        optionValue.classList.toggle("is-disabled", !hasAtLeastOneCombination);

        if (this.hideSoldOutVariants) {
          optionValue.toggleAttribute("hidden", !available);
        } else {
          // This will not disable sold out options but add a CSS class which we can potentially style in future
          optionValue.classList.toggle("is-sold-out", !available);
        }
      };

      const hasCombination = (variant, level2, value, selectedVariant2) => {
        return Array.from({ length: level2 + 1 }, (_, i) => {
          if (i === level2) {
            return variant[`option${level2 + 1}`] === value;
          } else {
            return variant[`option${i + 1}`] === selectedVariant2[`option${i + 1}`];
          }
        }).every((condition) => condition);
      };

      this.product["options"][level]["values"].forEach((value, valueIndex) => {
        const hasAtLeastOneCombination = this.product["variants"].some(
          (variant) => hasCombination(variant, level, value, selectedVariant) && variant
        );

        const hasAvailableVariant = this.product["variants"].some(
          (variant) => hasCombination(variant, level, value, selectedVariant) && variant["available"]
        );

        applyClassToSelector(this.optionSelectors[level], valueIndex, hasAvailableVariant, hasAtLeastOneCombination);
        this._updateDisableSelectorsForOptionLevel(level + 1, selectedVariant);
      });
    }
  };

  if (!window.customElements.get("variant-picker")) {
    window.customElements.define("variant-picker", VariantPicker);
  }


  var VariantOptionValue = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      this.innerHTML = event.detail.variant[this.getAttribute("for")];
    }
  };

  if (!window.customElements.get("variant-option-value")) {
    window.customElements.define("variant-option-value", VariantOptionValue);
  }


  var VariantSku = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      if (!event.detail.variant) {
        this.hidden = true;
      } else {
        this.innerText = `${window.themeVariables.strings.sku} ${event.detail.variant["sku"]}`;
        this.hidden = !event.detail.variant["sku"];
      }
    }
  };

  if (!window.customElements.get("variant-sku")) {
    window.customElements.define("variant-sku", VariantSku);
  }





  /*------------------------------------*\
    #QUANTITY
  \*------------------------------------*/

  var QuantitySelector = class extends HTMLElement {
    constructor() {
      super();
      this._decreaseQuantity;
      this._increaseQuantity;
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
      this._updateUI;
      this._abortController = undefined;
      this._decreaseButton = undefined;
      this._increaseButton = undefined;
      this._inputElement = undefined;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      this._decreaseButton = this.querySelector("button:first-of-type");
      this._increaseButton = this.querySelector("button:last-of-type");
      this._inputElement = this.querySelector("input");
      
      this._decreaseButton?.addEventListener("click", this._onDecreaseQuantity.bind(this), { signal: this._abortController.signal });
      this._increaseButton?.addEventListener("click", this._onIncreaseQuantity.bind(this), { signal: this._abortController.signal });

      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener.bind(this), { signal: this._abortController.signal });
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    _onDecreaseQuantity(event) {
      this._inputElement.stepDown();
      this._updateUI();
    }

    _onIncreaseQuantity(event) {
      this._inputElement.stepUp();
      this._updateUI();
    }

    _onVariantChanged(event) {
      this._inputElement.updateVariantRule(event.detail.variant);
    }

    _updateUI() {
      this._decreaseButton.disabled = this._inputElement.quantity <= 1;
    }
  };

  var QuantityInput = class extends HTMLInputElement {
    constructor() {
      super();
      this._onValueInputListener = this._onValueInput.bind(this);
      this.addEventListener("input", this._onValueInput);
      this.addEventListener("focus", this.select);
    }

    connectedCallback() {
      this.style.setProperty("--quantity-selector-character-count", `${this.value.length}ch`);
    }

    get quantity() {
      return parseInt(this.value);
    }

    updateVariantRule(variant) {
    }

    _onValueInput() {
      if (this.value === "") {
        this.value = this.min || 1;
      }

      this.style.setProperty("--quantity-selector-character-count", `${this.value.length}ch`);
    }
  };

  if (!window.customElements.get("quantity-selector")) {
    window.customElements.define("quantity-selector", QuantitySelector);
  }
  if (!window.customElements.get("quantity-input")) {
    window.customElements.define("quantity-input", QuantityInput, { extends: "input" });
  }




  /*------------------------------------*\
    #QUICK BUY
  \*------------------------------------*/

  var ProductQuickAdd = class extends HTMLElement {
    #scopeFromPassed = false;
    #scopeToReached = false;
    #intersectionObserver = new IntersectionObserver(this._onObserved.bind(this));

    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
      this.buttonScroller = Array.from(this.querySelectorAll(".button--scroller"));
      this.buttonScroller.forEach((element) => {
        element.addEventListener("click", this.buttonScrollerClicked.bind(this));
      });
    }

    buttonScrollerClicked(event) {
      const scrollToId = event.currentTarget.getAttribute("scroll-to");
      const scrollToElement = document.querySelector(`#${scrollToId}`);
      scrollToElement.scrollIntoView({ behavior: "smooth" });
    }

    connectedCallback() {
      this._scopeFrom = document.getElementById(this.getAttribute("form"));
      this._scopeTo = document.querySelector(".footer");
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);

      if (!this._scopeFrom || !this._scopeTo) {
        return;
      }

      this.#intersectionObserver.observe(this._scopeFrom);
      this.#intersectionObserver.observe(this._scopeTo);
    }

    disconnectedCallback() {
      this.#intersectionObserver.disconnect();
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;

      if (variant["available"]) {
        this.hidden = false;
      } else {
        this.hidden = true;
      }
    }

    _onObserved(entries) {
      entries.forEach((entry) => {
        if (entry.target === this._scopeFrom) {
          this.#scopeFromPassed = entry.boundingClientRect.bottom < 0;
        }

        if (entry.target === this._scopeTo) {
          this.#scopeToReached = entry.isIntersecting;
        }
      });

      this.classList.toggle("is-visible", this.#scopeFromPassed && !this.#scopeToReached);
    }
  };

  if (!window.customElements.get("product-quick-add")) {
    window.customElements.define("product-quick-add", ProductQuickAdd);
  }


  var QuickBuyDrawer = class extends Drawer {
    constructor() {
      super();
      this._hasLoaded = false;
      this.addEventListener("variant:add", this._onVariantAdded.bind(this));
    }

    async show() {
      this.style.display = "block";

      if (!this._hasLoaded) {
        [this, ...this.controls].forEach((control) => control.setAttribute("aria-busy", "true"));

        const responseContent = await (await fetch(`${window.Shopify.routes.root}products/${this.getAttribute("handle")}`)).text();

        [this, ...this.controls].forEach((control) => control.setAttribute("aria-busy", "false"));

        const quickBuyContent = new DOMParser().parseFromString(responseContent, "text/html").getElementById("quick-buy-content").content;

        Array.from(quickBuyContent.querySelectorAll("noscript")).forEach((noScript) => noScript.remove());

        this.replaceChildren(quickBuyContent);
        Shopify.PaymentButton?.init();
        this._hasLoaded = true;
      }

      var trustPilotWidget = this.querySelector(".trustpilot-widget");
      window.Trustpilot.loadFromElement(trustPilotWidget);

      return super.show();
    }

    async hide() {
      return super.hide()?.then(() => {
        this.style.display = "none";
      });
    }

    _onVariantAdded(event) {
      this.hide();
      this._hasLoaded = false;
    }
  };

  if (!window.customElements.get("quick-buy-drawer")) {
    window.customElements.define("quick-buy-drawer", QuickBuyDrawer);
  }






  /*------------------------------------*\
    #MEDIA
  \*------------------------------------*/

  var BaseMedia = class extends HTMLElement {
    static get observedAttributes() {
      return ["playing"];
    }

    connectedCallback() {
      this._abortController = new AbortController();
      if (this.hasAttribute("autoplay")) {
        Motion.inView(this, this.play.bind(this), { margin: "0px 0px 0px 0px" });
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get playing() {
      return this.hasAttribute("playing");
    }

    get player() {
      return this._playerProxy = this._playerProxy || new Proxy(this._playerTarget(), {
        get: (target, prop) => {
          return async () => {
            target = await target;
            this._playerHandler(target, prop);
          };
        }
      });
    }

    play() {
      if (!this.playing) {
        this.player.play();
      }
    }

    pause() {
      if (this.playing) {
        this.player.pause();
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "playing") {
        if (oldValue === null && newValue === "") {
          this.dispatchEvent(new CustomEvent("media:play", { bubbles: true }));

          if (this.hasAttribute("group")) {
            Array.from(document.querySelectorAll(`[group="${this.getAttribute("group")}"]`)).filter((item) => item !== this).forEach((itemToPause) => {
              itemToPause.pause();
            });
          }
        } else if (newValue === null) {
          this.dispatchEvent(new CustomEvent("media:pause", { bubbles: true }));
        }
      }
    }
  };


  var onYouTubePromise = new Promise((resolve) => {
    window.onYouTubeIframeAPIReady = () => resolve();
  });

  var VideoMedia = class extends BaseMedia {
    connectedCallback() {
      super.connectedCallback();
      if (!this.hasAttribute("autoplay")) {
        this.addEventListener("click", this.play, { once: true, signal: this._abortController.signal });
      }

      if (this.hasAttribute("show-play-button") && !this.shadowRoot) {
        this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment(`
          <slot></slot>
          
          <svg part="play-button" fill="none" width="48" height="48" viewBox="0 0 48 48">
            <path d="M48 24C48 10.745 37.255 0 24 0S0 10.745 0 24s10.745 24 24 24 24-10.745 24-24Z" fill="#f0f0f0"/>
            <path d="M18.578 32.629a.375.375 0 0 1-.578-.316V15.687c0-.297.328-.476.578-.316l12.931 8.314c.23.147.23.483 0 .63L18.578 32.63Z" fill="#1a1a1a"/>
            <path d="M24 .5C36.979.5 47.5 11.021 47.5 24S36.979 47.5 24 47.5.5 36.979.5 24 11.021.5 24 .5Z" stroke="#1a1a1a" stroke-opacity=".12"/>
          </svg>
        `));
      }
    }

    play({ restart = false } = {}) {
      if (restart && !this.hasAttribute("host")) {
        this.querySelector("video").currentTime = 0;
      }
      super.play();
    }

    _playerTarget() {
      if (this.hasAttribute("host")) {
        this.setAttribute("loaded", "");
        return new Promise(async (resolve) => {
          const templateElement = this.querySelector("template");

          if (templateElement) {
            templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
          }

          const muteVideo = this.hasAttribute("autoplay") || window.matchMedia("screen and (max-width: 999px)").matches;
          const script = document.createElement("script");
          script.type = "text/javascript";

          if (this.getAttribute("host") === "youtube") {
            if (!window.YT || !window.YT.Player) {
              script.src = "https://www.youtube.com/iframe_api";
              document.head.appendChild(script);
              await new Promise((resolve2) => {
                script.onload = resolve2;
              });
            }

            await onYouTubePromise;
            this.setAttribute("can-play", "");
            const player = new YT.Player(this.querySelector("iframe"), {
              events: {
                "onReady": () => {
                  if (muteVideo) {
                    player.mute();
                  }
                  resolve(player);
                },
                "onStateChange": (event) => {
                  if (event.data === YT.PlayerState.PLAYING) {
                    this.setAttribute("playing", "");
                  } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                    this.removeAttribute("playing");
                  }
                }
              }
            });
          }

          if (this.getAttribute("host") === "vimeo") {
            if (!window.Vimeo || !window.Vimeo.Player) {
              script.src = "https://player.vimeo.com/api/player.js";
              document.head.appendChild(script);
              await new Promise((resolve2) => {
                script.onload = resolve2;
              });
            }

            const player = new Vimeo.Player(this.querySelector("iframe"));
            if (muteVideo) {
              player.setMuted(true);
            }

            this.setAttribute("can-play", "");
            player.on("play", () => {
              this.setAttribute("playing", "");
            });

            player.on("pause", () => this.removeAttribute("playing"));
            player.on("ended", () => this.removeAttribute("playing"));
            resolve(player);
          }
        });
      } else {
        const videoElement = this.querySelector("video");
        this.setAttribute("loaded", "");
        this.setAttribute("can-play", "");

        videoElement.addEventListener("play", () => {
          this.setAttribute("playing", "");
          this.removeAttribute("suspended");
        });

        videoElement.addEventListener("pause", () => {
          if (!videoElement.seeking && videoElement.paused) {
            this.removeAttribute("playing");
          }
        });
        return videoElement;
      }
    }

    _playerHandler(target, prop) {
      if (this.getAttribute("host") === "youtube") {
        prop === "play" ? target.playVideo() : target.pauseVideo();
      } else {
        if (prop === "play" && !this.hasAttribute("host")) {
          target.play().catch((error) => {
            if (error.name === "NotAllowedError") {
              this.setAttribute("suspended", "");
              target.controls = true;
              const replacementImageSrc = target.previousElementSibling?.currentSrc;
              
              if (replacementImageSrc) {
                target.poster = replacementImageSrc;
              }
            }
          });
        } else {
          target[prop]();
        }
      }
    }
  };

  if (!window.customElements.get("video-media")) {
    window.customElements.define("video-media", VideoMedia);
  }





  /*------------------------------------*\
    #PRODUCT SECTIONS
  \*------------------------------------*/

  var ProductRecommendations = class extends HTMLElement {
    constructor() {
      super();
      this._isLoaded = false;
    }

    connectedCallback() {
      this._loadRecommendations();
    }

    async _loadRecommendations() {
      if (this._isLoaded) {
        return;
      }

      this._isLoaded = true;
      const section = this.closest(".shopify-section"); 
      const intent = this.getAttribute("intent") || "related";
      const url = `${Shopify.routes.root}recommendations/products?product_id=${this.getAttribute("product")}&limit=${this.getAttribute("limit") || 4}&section_id=${section.id.replace("shopify-section-", "")}&intent=${intent}`; 
      const response = await fetch(url, { priority: "low" });

      const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), productRecommendationsElement = tempDiv.querySelector("product-recommendations");

      if (productRecommendationsElement.childElementCount > 0) {
        this.replaceChildren(...document.importNode(productRecommendationsElement, true).childNodes);

        var trustPilotWidgets = this.getElementsByClassName("trustpilot-widget");
        for(var i=0; i<trustPilotWidgets.length; i++) {
          window.Trustpilot.loadFromElement(trustPilotWidgets[i]);
        }
      } else {
        if (intent === "related") {
          section.remove();
        } else {
          this.remove();
        }
      }
    }
  };

  if (!window.customElements.get("product-recommendations")) {
    window.customElements.define("product-recommendations", ProductRecommendations);
  }


  var RecentlyViewedProducts = class extends HTMLElement {
    constructor() {
      super();
      this._isLoaded = false;
    }

    connectedCallback() {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(this._loadProducts.bind(this), { timeout: 1500 });
      } else {
        this._loadProducts();
      }
    }

    removeAllItems(event) {
      localStorage.removeItem('theme:recently-viewed-products');

      this._isLoaded = false;
      this._loadProducts();
    }

    removeItem(event) {
      const recentlyViewedProducts = new Set(JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]'));

      const productId = parseInt(event.currentTarget.getAttribute("product-id"));
      
      recentlyViewedProducts.delete(productId);

      // Update localStorage
      localStorage.setItem('theme:recently-viewed-products', JSON.stringify(Array.from(recentlyViewedProducts.values())));

      // Reload the section
      this._isLoaded = false;
      this._loadProducts();
    }
  
    get _searchQueryString() {
      const items = new Set(JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]"));

      if (this.hasAttribute("exclude-id")) {
        items.delete(parseInt(this.getAttribute("exclude-id")));
      }

      return Array.from(items.values(), item => `id:${item}`).slice(0, parseInt(this.getAttribute("products-count"))).join(" OR ");
    }
  
    async _loadProducts() {
      if (this._isLoaded) {
        return;
      }
      
      this._isLoaded = true;
  
      const section = this.closest(".shopify-section");
      const url = `${Shopify.routes.root}search?type=product&q=${this._searchQueryString}&section_id=${extractSectionId(section)}`;
      const response = await fetch(url, { priority: "low" });
      const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html");
      const recentlyViewedProductsElement = tempDiv.querySelector("recently-viewed-products");
  
      if (recentlyViewedProductsElement.childElementCount > 0) {
        this.replaceChildren(...document.importNode(recentlyViewedProductsElement, true).childNodes);

        var trustPilotWidgets = this.getElementsByClassName("trustpilot-widget");
        for(var i=0; i<trustPilotWidgets.length; i++) {
          window.Trustpilot.loadFromElement(trustPilotWidgets[i]);
        }

        // Now that the content is loaded, query the remove buttons add the event listeners.
        this.removeButtons = Array.from(this.querySelectorAll(".remove-button"));
        this.removeButtons.forEach((button) => button.addEventListener("click", this.removeItem.bind(this)));
        this.removeAllButton = this.querySelector(".remove-all-button");
        this.removeAllButton.addEventListener("click", this.removeAllItems.bind(this))
      } else {
        section.remove();
      }
    }
  }
  
  if (!window.customElements.get("recently-viewed-products")) {
    window.customElements.define("recently-viewed-products", RecentlyViewedProducts);
  }

  /**
   * Product page Back in Stock functionality integrated with Klaviyo.
   * We run two separate fetches as Klaviyo has separate APIs for back in stock and subscription.
   */
  var BackInStock = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
      this.form = this.querySelector(".back-in-stock__form");
      this.form.addEventListener("submit", this._onFormSubmitted.bind(this));
      this.formInput = this.querySelector('input[type="email"]');
      this.formCheckbox = this.querySelector('input[type="checkbox"]');
      this.resultsElement = this.querySelector(".back-in-stock__results");
      this.submitButton = this.querySelector('button[type="submit"]');
      this.apiKey = this.getAttribute("api-key");
      this.listId = this.getAttribute("list-id");
      this.successText = this.getAttribute("success-text");
    }

    connectedCallback() {
      this.productOffers = document.querySelector(".product-info__offers");
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    get checkboxChecked() {
      return this.formCheckbox.checked;
    }

    async _onFormSubmitted(event) {
      event.preventDefault();
      this.resultsElement.hidden = true;
      this.resultsElement.innerHTML = "";

      const email = this.formInput.value;
      const variant = this.form.getAttribute("data-variant-id");

      this.submitButton.setAttribute("aria-busy", "true");

      const backInStockUrl = "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=" + this.apiKey;

      const payload = {
        "data": {
          "type": "back-in-stock-subscription",
          "attributes": {
            "profile": {
              "data": {
                  "type": "profile",
                  "attributes": {
                      "email": email,
                  }
              }
            },
            "channels": ["EMAIL"],
          },
          "relationships": {
            "variant": {
              "data": {
                "type": "catalog-variant",
                "id": "$shopify:::$default:::" + variant,
              }
            }
          }
        }
      }

      var requestOptions = {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          revision: '2025-01-15',
          'content-type': 'application/vnd.api+json'
        },
        body: JSON.stringify(payload),
      };

      const response = await fetch(backInStockUrl, requestOptions);

      if (response.ok) {
        this.resultsElement.innerHTML = `<span class="text-success">${this.successText}</span>`;
        this.resultsElement.hidden = false;
      } else {
        const errors = await response.json();
        this.resultsElement.innerHTML = `<span class="text-error">${errors["errors"][0]["detail"]}</span>`;
        this.resultsElement.hidden = false;
      }

      if (this.checkboxChecked) {
        const newsletterUrl = "https://a.klaviyo.com/client/subscriptions/?company_id=" + this.apiKey;

        const secondPayload = { 
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Back in stock form',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email
                  },
                }
              }
            },
            relationships: {list: {data: {type: 'list', id: `${this.listId}`}}}
          }
        }

        var secondRequestOptions = {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            "revision": "2024-06-15"
          },
          body: JSON.stringify(secondPayload),
        };

        const secondResponse = await fetch(newsletterUrl, secondRequestOptions);

        if (secondResponse.ok) {
          console.log(secondResponse);
        } else {
          const secondErrors = await secondResponse.json();
          console.log(secondErrors);
        }
      }

      this.submitButton.setAttribute("aria-busy", "false");
    }

    _onVariantChanged(event) {
      const variant = event.detail.variant;

      if (variant["available"]) {
        this.hidden = true;
        this.productOffers.hidden = false;
        this.form.setAttribute("data-variant-id", "");
      } else {
        this.form.setAttribute("data-variant-id", variant["id"]);
        this.productOffers.hidden = true;
        this.hidden = false;
      }

    }
  }

  if (!window.customElements.get("back-in-stock")) {
    window.customElements.define("back-in-stock", BackInStock);
  }


  /**
   * Product page alternative variants. This checks for the selected variant, and will show alternative variants. The primary logic is within Liquid. The JS is primarily controlling showing/hiding the alternatives.
   */
  var AlternativeVariants = class extends HTMLElement {
    constructor() {
      super();

      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      this._productForm = document.forms[this.getAttribute("form")];
      this._productForm?.addEventListener("variant:change", this._onVariantChangedListener);
      this.json = this.querySelector(".alternative-variants__json");
      this.badges = this.querySelector(".alternative-variants__badge-list");
      this.button = this.querySelector(".alternative-variants__button");
      this._onLoad();
    }

    get avLink() {
      return this.getAttribute("av-link");
    }

    get productHandle() {
      return this.getAttribute("handle");
    }

    _onLoad() {
      if (this.avLink) {
        this._fetchAlternativeVariants(this.avLink);
      }
    }

    _fetchAlternativeVariants(url) {
      try {
        return this._loadAlternativeVariants(url);
      } catch (e) {
        if (e.name !== "AbortError") {
          throw e;
        }
      }
    }

    async _loadAlternativeVariants(url) {
      const newElement = new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}${url}&view=fetch&section_id=fetch-collection`)).text(), "text/html");
  
      const altVariantsResults = this.querySelector(".alternative-variants__list");
      const collectionResultsChildren = newElement.querySelector(".collection-results").children;
  
      altVariantsResults.innerHTML = "";

      /**
       * We want a maximum of 3 alternative variants to show. However, this may include our existing product which is why set maxImported to 4. We also need to check if any products are available to display which determines whether we show the widget.
       */

      let importedCount = 0;
      let displayedCount = 0;
      const maxImported = 4;
      const maxDisplayed = 3;
      
      for (const child of collectionResultsChildren) {
        if (importedCount >= maxImported) break;

        const handle = child.getAttribute("handle");

        if (handle === this.productHandle) {
          importedCount++;
          continue;
        }

        if (displayedCount < maxDisplayed) {
          altVariantsResults.appendChild(document.importNode(child, true));
          displayedCount++;
        }
    
        importedCount++;
      }

      if (displayedCount > 0) {
        this.button.href = `${window.Shopify.routes.root}${url}`;
        this.removeAttribute("hidden");
      }

      var trustPilotWidgets = this.getElementsByClassName("trustpilot-widget");
      for(var i=0; i<trustPilotWidgets.length; i++) {
          window.Trustpilot.loadFromElement(trustPilotWidgets[i]);
      }
    }

    _onVariantChanged(event) {
      this.setAttribute("hidden", "");

      if (!event.detail.variant.available) {
        const jsonData = JSON.parse(this.json.textContent);
        const newurl = jsonData[event.detail.variant.id];
        this._fetchAlternativeVariants(newurl);
        let newBadgesContent = ``
        
        event.detail.variant.options.forEach((element) => {
          newBadgesContent += `<div class="badge badge--primary">${element}</div>`
        });

        this.badges.innerHTML = "";
        this.badges.innerHTML = newBadgesContent;
      }
    }
  }

  if (!window.customElements.get("alternative-variants")) {
    window.customElements.define("alternative-variants", AlternativeVariants);
  }


  var ProductZoomModal = class extends Modal {
    constructor() {
      super();
      this.addEventListener("dialog:after-hide", () => {
        this.querySelector("video-media").pause();
        this.querySelector("product-zoom-media-carousel").select(0)
      });
    }

    connectedCallback() {
      super.connectedCallback();
    }

    get shouldAppendToBody() {
      return false;
    }
  }

  if (!window.customElements.get("product-zoom-modal")) {
    window.customElements.define("product-zoom-modal", ProductZoomModal);
  }

  var ProductGallery = class extends HTMLElement {
    constructor() {
      super();
      this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
      document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);

      this._carousels = Array.from(this.querySelectorAll("media-carousel"));
      this._pageDots = Array.from(this.querySelectorAll("page-dots"));
      this.productGalleryInfo = this.querySelector(".product-gallery__info");

      if (this.productGalleryInfo) {
        this.addEventListener("carousel:change", this._updateCarouselInformation);
      }

      this.addEventListener("lightbox:open", (event) => this.openZoom(event.detail.index));
    }

    _updateCarouselInformation(event) {
      if (event.detail.slide.getAttribute("data-media-type") === "image") {
        this.productGalleryInfo.classList.remove("is-hidden");
      } else {
        this.productGalleryInfo.classList.add("is-hidden");
      }
    }

    disconnectedCallback() {
      document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
      const filteredIndexes = this._getFilteredMediaIndexes(event.detail.product, event.detail.variant);

      this._carousels.forEach((carousel) => carousel.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes } })));

      this._pageDots.forEach((pageDots) => pageDots.dispatchEvent(new CustomEvent("control:filter", { detail: { filteredIndexes } })));

      if (event.detail.variant["featured_media"] && event.detail.previousVariant["featured_media"] && event.detail.previousVariant["featured_media"]["id"] !== event.detail.variant["featured_media"]["id"] || !event.detail.previousVariant["featured_media"] && event.detail.variant["featured_media"]) {
        this._carousels.forEach((carousel) => carousel.select(event.detail.variant["featured_media"]["position"] - 1, { animate: false }));
      }
    }

    _getFilteredMediaIndexes(product, variant) {
      const filteredMediaIds = [];

      product["media"].forEach((media) => {
        let matchMedia = variant["featured_media"] && media["position"] === variant["featured_media"]["position"];

        if (media["alt"]?.includes("#") && media["alt"] !== product["title"]) {
          if (!matchMedia) {
            const altParts = media["alt"].split("#"), mediaGroupParts = altParts.pop().split("_");

            product["options"].forEach((option) => {
              if (option["name"].toLowerCase() === mediaGroupParts[0].toLowerCase()) {
                if (variant["options"][option["position"] - 1].toLowerCase() !== mediaGroupParts[1].trim().toLowerCase()) {
                  filteredMediaIds.push(media["position"] - 1);
                }
              }
            });
          }
        }
      });

      return filteredMediaIds;
    }

    openZoom(index = 0) {
      this.querySelector("product-zoom-media-carousel").select(index);
      this.querySelector("product-zoom-modal").show();
    } 
  }

  if (!window.customElements.get("product-gallery")) {
    window.customElements.define("product-gallery", ProductGallery);
  }

  var ProductZoomGallery = class extends ProductGallery {
    connectedCallback() {
      super.connectedCallback();
      this.activeSlideCount = document.querySelector("product-zoom-modal .pagination__active-count");
      this.productInfoText = Array.from(document.querySelectorAll(".zoom-modal__info"));
      this.addEventListener("carousel:change", this._updateCarouselInformation);
    }

    _updateCarouselInformation(event) {
      this.activeSlideCount.innerText = event.detail.index + 1;
      if (event.detail.slide.getAttribute("data-media-type") === "image") {
        this.productInfoText.forEach((element) => {
          element.classList.remove("is-hidden");
        });
      } else {
        this.productInfoText.forEach((element) => {
          element.classList.add("is-hidden");
        });
      }
    }
  }

  if (!window.customElements.get("product-zoom-gallery")) {
    window.customElements.define("product-zoom-gallery", ProductZoomGallery);
  }

  var MediaCarousel = class extends EffectCarousel {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener("carousel:settle", this._onMediaSettled, { signal: this._abortController.signal });
    }

    get loop() {
      return false;
    }

    _onMediaSettled(event) {
      const media = event.detail.slide;
      this.items.filter((item) => ["video", "external_video", "model"].includes(item.getAttribute("data-media-type"))).forEach((item) => item.firstElementChild.pause());
      switch (media.getAttribute("data-media-type")) {
        case "external_video":
        case "video":
          if (this.hasAttribute("media-autoplay")) {
            media.firstElementChild.play();
          }
          break;
      }
    }
  }

  if (!window.customElements.get("media-carousel")) {
    window.customElements.define("media-carousel", MediaCarousel);
  }

  var ProductZoomMediaCarousel = class extends MediaCarousel {
    connectedCallback() {
      super.connectedCallback();
    }
  }

  if (!window.customElements.get("product-zoom-media-carousel")) {
    window.customElements.define("product-zoom-media-carousel", ProductZoomMediaCarousel);
  }

  var ProductZoomButton = class extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener("click", this._onButtonClicked);
    }

    _onButtonClicked() {
      let media = this.closest(".product-gallery__media");
      let openIndex;

      if (media) {
        const visibleImages = this.closest("media-carousel").visibleItems.filter((item) => item.getAttribute("data-media-type") === "image");
        openIndex = visibleImages.indexOf(media);
      } else {
        const carousel = this.closest(".product-gallery__media-list-wrapper").querySelector("media-carousel");
        const visibleImages = carousel.visibleItems.filter((item) => item.getAttribute("data-media-type") === "image");
        openIndex = visibleImages.indexOf(carousel.selectedSlide);
      }

      this.dispatchEvent(new CustomEvent("lightbox:open", {
        bubbles: true,
        detail: {
          index: openIndex
        }
      }));      
    }
  }

  if (!window.customElements.get("product-zoom-button")) {
    window.customElements.define("product-zoom-button", ProductZoomButton, { extends: "button" });
  }

  // create a container and set the full-size image as its background
  function createOverlay(image) {
    const overlayImage = document.createElement('img');
    overlayImage.setAttribute('src', `${image.src}`);
    overlay = document.createElement('div');
    prepareOverlay(overlay, overlayImage);

    image.style.opacity = '50%';
    toggleLoadingSpinner(image);

    overlayImage.onload = () => {
      toggleLoadingSpinner(image);
      image.parentElement.insertBefore(overlay, image);
      image.style.opacity = '100%';
    };

    return overlay;
  }

  function prepareOverlay(container, image) {
    container.setAttribute('class', 'image-magnify-full-size');
    container.setAttribute('aria-hidden', 'true');
    container.style.backgroundImage = `url('${image.src}')`;
  }

  function toggleLoadingSpinner(image) {
    const loadingSpinner = image.parentElement.parentElement.querySelector(`.loading__spinner`);
    loadingSpinner.classList.toggle('hidden');
  }

  function moveWithHover(image, event, zoomRatio) {
    // calculate mouse position
    const ratio = image.height / image.width;
    const container = event.target.getBoundingClientRect();
    const xPosition = event.clientX - container.left;
    const yPosition = event.clientY - container.top;
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

    // determine what to show in the frame
    overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    overlay.style.backgroundSize = `${image.width * zoomRatio}px`;
  }

  function magnify(image, zoomRatio) {
    const overlay = createOverlay(image);
    overlay.onclick = () => overlay.remove();
    overlay.onmousemove = (event) => moveWithHover(image, event, zoomRatio);
    overlay.onmouseleave = () => overlay.remove();
  }

  function enableZoomOnHover(zoomRatio) {
    const images = document.querySelectorAll('.image-magnify-hover');
    images.forEach((image) => {
      image.onclick = (event) => {
        magnify(image, zoomRatio);
        moveWithHover(image, event, zoomRatio);
      };
    });
  }

  enableZoomOnHover(2);

  /*------------------------------------*\
    #IMAGE SECTIONS
  \*------------------------------------*/

  var MultipleImagesWithText = class extends HTMLElement {
    constructor() {
      super();
      this._imageCarousel = this.querySelector("multiple-images-with-text-image-list");
      this._contentCarousel = this.querySelector("multiple-images-with-text-content-list");

      this.addEventListener("control:prev", () => {
        this._imageCarousel.previous();
        this._contentCarousel.previous();
      });

      this.addEventListener("control:next", () => {
        this._imageCarousel.next();
        this._contentCarousel.next();
      });

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          if (event.target.hasAttribute("image-id")) {
            this._imageCarousel.select(this._imageCarousel.items.findIndex((item) => item.id === event.target.getAttribute("image-id")));
          }
        });
      }
    }
  };

  var MultipleImagesWithTextImageList = class extends EffectCarousel {
    constructor() {
      super();
      if (window.ResizeObserver) {
        new ResizeObserver(throttle(this._adjustHeight.bind(this))).observe(this);
      }
    }

    _transitionTo(fromSlide, toSlide, { direction, animate: animate11 }) {
      if (this.getAttribute("layout") !== "stacked") {
        return;
      }

      const transitionSpeed = 0.2;

      if (direction === "next") {
        const fromSlideTransform = getComputedStyle(fromSlide).getPropertyValue("transform");

        return Motion.timeline([
          [fromSlide, { opacity: 0 }, { duration: transitionSpeed }],
          [toSlide, { zIndex: 1 }, { duration: transitionSpeed, zIndex: { easing: "step-start" } }],
          [fromSlide, { opacity: 1, zIndex: -1 }, { duration: transitionSpeed, at: "<", zIndex: { easing: "step-start" } }],
          [toSlide.previousElementSibling, { zIndex: 0 }, { at: "<", easing: "step-start" }]
        ], { defaultOptions: { easing: [0.26, 0.02, 0.27, 0.97] } }).finished;
      } else {
        const toSlideTransform = getComputedStyle(toSlide).getPropertyValue("transform");

        return Motion.timeline([
          [toSlide, { opacity: 0 }, { duration: transitionSpeed }],
          this.items.length >= 3 && [fromSlide.previousElementSibling || this.lastElementChild, { zIndex: -1 }, { easing: "step-start" }],
          [toSlide, { opacity: 1, zIndex: 1 }, { duration: transitionSpeed, at: this.items.length >= 3 ? "<" : "+0", zIndex: { easing: "step-start" } }],
          [fromSlide, { zIndex: 0 }, { duration: transitionSpeed, at: "<", zIndex: { easing: "step-start" } }]
        ].filter(Boolean), { defaultOptions: { easing: [0.26, 0.02, 0.27, 0.97] } }).finished;
      }
    }
  };

  var MultipleImagesWithTextContentList = class extends EffectCarousel {
    constructor() {
      super();
    }

    _transitionTo(fromSlide, toSlide, { direction = "next", animate: animate11 = true } = {}) {
      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");

      return Motion.timeline([
        [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"] }],
        [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"] }]
      ], { duration: animate11 ? parseFloat(this.getAttribute("fade-speed") || 0.5) : 0 }).finished;
    }
  };

  if (!window.customElements.get("multiple-images-with-text")) {
    window.customElements.define("multiple-images-with-text", MultipleImagesWithText);
  }

  if (!window.customElements.get("multiple-images-with-text-image-list")) {
    window.customElements.define("multiple-images-with-text-image-list", MultipleImagesWithTextImageList);
  }

  if (!window.customElements.get("multiple-images-with-text-content-list")) {
    window.customElements.define("multiple-images-with-text-content-list", MultipleImagesWithTextContentList);
  }

  var ImageLinkBlocks = class extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.children);
      new ScrollArea(this);
      this.addEventListener("control:prev", this._prev);
      this.addEventListener("control:next", this._next);
    }

    _prev() {
      this.scrollBy({ left: (document.dir === "rtl" ? 1 : -1) * this.items[0].clientWidth, behavior: "smooth" });
    }
    _next() {
      this.scrollBy({ left: (document.dir === "rtl" ? -1 : 1) * this.items[0].clientWidth, behavior: "smooth" });
    }
  };
  
  if (!window.customElements.get("image-link-blocks")) {
    window.customElements.define("image-link-blocks", ImageLinkBlocks);
  }

  /**
   * IMPORTANT: This is no longer being used for the collection SEO block as it depended on truncation within Liquid which caused issues with HTML content.
   * 
   * A custom element for "read more"/"read less" functionality. We essentially have duplicate elements (a truncated and full version). On clicking "read more" we swap in the full element.
   */
  var ContentSwap = class extends HTMLElement {
    constructor() {
      super();

      this.shortContent = this.querySelector(".content-swap__short");
      this.fullContent = this.querySelector(".content-swap__full");
      this.moreButton = this.querySelector('[data-action="show-more"]');
      this.lessButton = this.querySelector('[data-action="show-less"]');

      this.lessButton.addEventListener("click", this.showLess.bind(this));
      this.moreButton.addEventListener("click", this.showMore.bind(this));
    }

    showLess(event) {
      event.preventDefault();
      this.fullContent.style.display = 'none';
      this.shortContent.style.display = 'block';
    }

    showMore(event) {
      event.preventDefault();
      this.shortContent.style.display = 'none';
      this.fullContent.style.display = 'block';
    }
  };

  if (!window.customElements.get("content-swap")) {
    window.customElements.define("content-swap", ContentSwap);
  }

  /**
   * A new component used for "read more"/"read less" Collection SEO functionality. This is dependent on a fix height and javascript so no truncation is required meaning any HTML content is unaffected.
   */
  class ExpandableContent extends HTMLElement {
    constructor() {
      super();
  
      // Get the toggle button and main content
      this.expandableToggleButton = this.querySelector(".expandable-content__toggle");
      this.mainContent = this.querySelector(".expandable-content__main");
      this.expanded = false; // Track the current state (collapsed by default)
      
      // Set initial ARIA attributes
      this.expandableToggleButton.setAttribute("aria-expanded", "false");
      this.expandableToggleButton.setAttribute("aria-controls", this.mainContent.id || "expandable-content-main");
      this.mainContent.setAttribute("aria-hidden", "true");
  
      // Bind the click handler to the toggle button
      this.expandableToggleButton.addEventListener("click", () => this.toggleContent());
    }
  
    connectedCallback() {
      // Check if the content height is greater than the max allowed height (320px)
      if (this.mainContent.scrollHeight > 320) {
        this.classList.add("expandable-content--expandable");
      } else {
        this.classList.remove("expandable-content--expandable");
      }
    }
  
    toggleContent() {
      if (this.expanded) {
        this.collapseContent();
      } else {
        this.expandContent();
      }
    }
  
    expandContent() {
      this.mainContent.style.height = `${this.mainContent.scrollHeight}px`; // Set height to scrollHeight temporarily for smooth transition
      this.mainContent.style.height = 'auto'; // Set height to auto after transition
      this.classList.add("expandable-content--expanded");
      this.expanded = true;
  
      // Update ARIA attributes
      this.expandableToggleButton.setAttribute("aria-expanded", "true");
      this.mainContent.setAttribute("aria-hidden", "false");
    }
  
    collapseContent() {
      this.mainContent.style.height = "320px"; // Set the height to the initial collapsed height
      this.classList.remove("expandable-content--expanded");
      this.expanded = false;
  
      // Update ARIA attributes
      this.expandableToggleButton.setAttribute("aria-expanded", "false");
      this.mainContent.setAttribute("aria-hidden", "true");
      this.mainContent.scrollIntoView({ behavior: "smooth"});
    }
  }
  
  if (!window.customElements.get("expandable-content")) {
    window.customElements.define("expandable-content", ExpandableContent);
  }  

  /**
   * Accordion-like elements for the Shop The Look section. We run most animations in JavaScript but we also have a `transition-class` as a CSS hook as there are other complex changes within the design that are best done within the CSS such as borders and pseudoelements.
   */
  var ShopTheLookAccordion = class extends HTMLElement {
    static get observedAttributes() {
      return ["open"];
    }

    constructor() {
      super();
      this.summaryElement = this.querySelector('.shop-the-look-accordion__toggle');
      this.contentElement = this.querySelector('.shop-the-look-accordion__content');
      this._open = this.hasAttribute("open");
      this.summaryElement.addEventListener("click", this._onSummaryClicked.bind(this));
      this.setAttribute("aria-expanded", this._open ? "true" : "false");

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", () => this.open = true);
        this.addEventListener("shopify:block:deselect", () => this.open = false);
      }
    }

    set open(value) {
      if (value !== this._open) {
        this._open = value;
        if (this.isConnected) {
          this._transition(value);
        } else {
          value ? this.setAttribute("open", "") : this.removeAttribute("open");
        }
      }

      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get open() {
      return this._open;
    }

    get collapseNonActiveElements() {
      return this.hasAttribute("collapse-non-active-elements");
    }

    get scrollToActive() {
      return this.hasAttribute("scroll-to-active");
    }

    get addTransitionClass() {
      return this.hasAttribute("transition-class");
    }

    get transitionClass() {
      return this.getAttribute("transition-class");
    }

    scrollToActiveElement(element) {
      if (window.matchMedia("(max-width: 999px)").matches) {
        element.scrollIntoView({ behavior: "smooth"});
      }
    }

    async _transition(value) {
      this.contentElement.style.overflow = "hidden";
      if (value) {
        if (this.addTransitionClass) {
          this.classList.add(this.transitionClass);
        }
        this.setAttribute("open", "");
        await Motion.timeline([
          [this.contentElement, { height: [0, `${this.contentElement.scrollHeight}px`] }, { duration: 0.25, easing: "ease" }],
          [this.contentElement, { opacity: [0, 1], transform: ["translateY(0)", "translateY(-4px)"] }, { duration: 0.15, at: "-0.1" }]
        ]).finished;

        this.contentElement.style.height = "auto";
        this.contentElement.style.overflow = "visible";

        if (this.scrollToActive) {
          this.scrollToActiveElement(this);
        }

        this.dispatchEvent(new CustomEvent("openable-element:open", {
          bubbles: true
        }));
      } else {
        if (this.addTransitionClass) {
          this.classList.remove(this.transitionClass);
        }

        await Motion.timeline([
          [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
          [this.contentElement, { height: [`${this.contentElement.clientHeight}px`, 0] }, { duration: 0.25, at: "<", easing: "ease" }]
        ]).finished;

        this.contentElement.style.height = "0";
        this.contentElement.style.overflow = "hidden";

        this.dispatchEvent(new CustomEvent("openable-element:close", {
          bubbles: true
        }));
        
        this.removeAttribute("open");
      }
    }

    _onSummaryClicked(event, controlledElement = this, externalClick) {
      event.preventDefault();
      if (controlledElement.collapseNonActiveElements) {
        Array.from(controlledElement.parentElement.children).forEach((button, index) => {
          if (button != controlledElement) {
            button.open = false;
          }
        });

        controlledElement.open = !controlledElement.open;
      } else {
        controlledElement.open = !controlledElement.open;
      }
    }
    
    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "open") {
        this.setAttribute("aria-expanded", newValue === "" ? "true" : "false");
      }
    }
  }

  if (!window.customElements.get("shop-the-look-accordion")) {
    window.customElements.define("shop-the-look-accordion", ShopTheLookAccordion);
  }

  var ImageTextSlideshow = class extends EffectCarousel {
    _pendingAnimationControls = [];

    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback();

      if (this._player && this.hasAttribute("autoplay") && window.matchMedia("screen and (min-width: 1000px)").matches) {
        this._player.addEventListener("player:start", (event) => {
          const circles = Array.from(this.querySelectorAll(".numbered-dots__item"));

          circles.forEach((item) => {
            const circle = item.querySelector("circle:last-child");

            let circleAnimationControl = null;

            if (item.parentElement.parentElement.getAttribute("aria-current") === "true") {
              circleAnimationControl = Motion.animate(circle, { strokeDasharray: [`0px, ${circle.getTotalLength()}px`, `${circle.getTotalLength()}px, ${circle.getTotalLength()}px`] }, { duration: event.detail.duration, easing: "linear" });
            } else {
              circleAnimationControl = Motion.animate(circle, { strokeDasharray: `${circle.getTotalLength()}px, ${circle.getTotalLength()}px` }, { duration: 0, easing: "linear" });
            }

            this._pendingAnimationControls.push(circleAnimationControl);
          });

          this._player.addEventListener("player:pause", () => {
            this._pendingAnimationControls.forEach((control) => control.pause());
          });

          this._player.addEventListener("player:resume", () => {
            this._pendingAnimationControls.forEach((control) => control.play());
          });
        })
      }
    }
  }

  if (!window.customElements.get("image-text-slideshow")) {
    window.customElements.define("image-text-slideshow", ImageTextSlideshow);
  }

  var VyneOnlineSwitcher = class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.slideshows = Array.from(this.querySelectorAll(".image-text-slideshow"));
      this.select = this.querySelector("#vyne-online-switcher");
      this.select.addEventListener("change", this._onOptionChanged.bind(this));
      this.currentElement = this.querySelector(".image-text-slideshow:not(.hidden)");

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          this.currentElement.querySelector("effect-carousel").player?.pause();
          const optionSelected = event.target.parentElement.getAttribute("group");

          this.slideshows.forEach((element) => {
            element.classList.add("hidden");
    
            if (element.id == optionSelected) {
              element.classList.remove("hidden");
    
              if (window.matchMedia("screen and (min-width: 1000px)").matches) {
                element.querySelector("effect-carousel").player?.resume();
              }
              this.currentElement = element;
            }
          });
        });
      }
    }

    _onOptionChanged(event) {
      this.currentElement.querySelector("effect-carousel").player?.pause();
      const optionSelected = event.target.value;

      this.slideshows.forEach((element) => {
        element.classList.add("hidden");

        if (element.id == optionSelected) {
          element.classList.remove("hidden");

          if (window.matchMedia("screen and (min-width: 1000px)").matches) {
            element.querySelector("effect-carousel").player?.resume();
          }
          this.currentElement = element;
        }
      });
    }
  }

  if (!window.customElements.get("vyne-online-switcher")) {
    window.customElements.define("vyne-online-switcher", VyneOnlineSwitcher);
  }

  var ImageWithDownload = class extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector(".form");
      this.form.addEventListener("submit", this._onFormSubmitted.bind(this));
      this.formInput = this.querySelector('input[type="email"]');
      this.formCheckbox = this.querySelector('input[type="checkbox"]');
      this.resultsElement = this.querySelector(".form__results");
      this.submitButton = this.querySelector('button[type="submit"]');
      this.apiKey = this.getAttribute("api-key");
      this.listId = this.getAttribute("list-id");
      this.successText = this.getAttribute("success-text");
      this.fileUrl = this.getAttribute("file-url"); // Attribute for file download URL
    }

    get checkboxChecked() {
      return this.formCheckbox.checked;
    }

    async _onFormSubmitted(event) {
      event.preventDefault();
      this.resultsElement.hidden = true;
      this.resultsElement.innerHTML = "";

      const email = this.formInput.value;

      this.submitButton.setAttribute("aria-busy", "true");

      if (this.checkboxChecked) {
        const newsletterUrl = "https://a.klaviyo.com/client/subscriptions/?company_id=" + this.apiKey;

        const payload = { 
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Image with download form',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email
                  },
                }
              }
            },
            relationships: {list: {data: {type: 'list', id: `${this.listId}`}}}
          }
        }

        var requestOptions = {
          method: 'POST',
          headers: {
            revision: '2025-01-15', 
            'content-type': 'application/vnd.api+json'
          },
          body: JSON.stringify(payload),
        };

        const response = await fetch(newsletterUrl, requestOptions);

        if (response.ok) {
          console.log(response);
          this.resultsElement.innerHTML = `<span class="text-success">${this.successText}</span>`;
          this.resultsElement.hidden = false;
        } else {
          const errors = await response.json();
          console.log(errors);
          this.resultsElement.innerHTML = `<span class="text-error">${errors["errors"][0]["detail"]}</span>`;
          this.resultsElement.hidden = false;
        }
      }

      // Trigger file download irrespective of the Klaviyo API response
      this._downloadFile();

      this.submitButton.setAttribute("aria-busy", "false");
    }
    
    _downloadFile() {
      // Create an XMLHttpRequest to fetch the file
      fetch(this.fileUrl)
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = this.fileUrl.split('/').pop(); // Use the file name from the URL for download
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url); // Clean up the URL object
        })
        .catch(error => console.error('File download failed:', error));
    }
  }

  if (!window.customElements.get("image-with-download")) {
    window.customElements.define("image-with-download", ImageWithDownload);
  }


  /*------------------------------------*\
    #BLOG SECTIONS
  \*------------------------------------*/

  var BlogEffectCarousel = class extends EffectCarousel {
    connectedCallback() {
      super.connectedCallback();

      let tagHandle = this.selectedSlide.getAttribute("tag-handle");
      let element = this.selectedSlide;
      let numberOfPosts = parseInt(this.selectedSlide.getAttribute("number-of-posts"));

      _fetchBlogPosts(tagHandle, element, numberOfPosts);

      this.addEventListener("carousel:select", () => {
        tagHandle = this.selectedSlide.getAttribute("tag-handle");
        element = this.selectedSlide;
        numberOfPosts = parseInt(this.selectedSlide.getAttribute("number-of-posts"));
        _fetchBlogPosts(tagHandle, element, numberOfPosts)
      }, { signal: this._abortController.signal });
    }

    _fetchBlogPosts() {
      if (!Shopify.designMode) {
        if (!this.selectedSlide.hasAttribute("tag-handle") || this.selectedSlide.hasAttribute("blog-posts-loaded")) {
          return;
        }
      }

      const tagHandle = this.selectedSlide.getAttribute("tag-handle");

      try {
        return this._loadBlog(tagHandle);
      } catch (e) {
        if (e.name !== "AbortError") {
          throw e;
        }
      }
    }

    async _loadBlog(tagHandle) {
      const newElement = new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}blogs/support-advice/tagged/${tagHandle}?view=fetch&section_id=fetch-blog`, { signal: this._abortController.signal })).text(), "text/html");

      const blogPostsResults = this.selectedSlide.querySelector(".blog-post-list");
      const blogResultsChildren = newElement.querySelector(".blog-results").children;
      const buttonWrapper = this.selectedSlide.querySelector(".blog-posts__button-wrapper");

      blogPostsResults.innerHTML = "";

      for (const child of blogResultsChildren) {
        blogPostsResults.appendChild(document.importNode(child, true));
      }

      buttonWrapper.style.opacity = "1";

      this.selectedSlide.setAttribute("blog-posts-loaded","");
    }
  }

  if (!window.customElements.get("blog-effect-carousel")) {
    window.customElements.define("blog-effect-carousel", BlogEffectCarousel);
  }

  /*------------------------------------*\
    #SWITCH ELEMENTS
  \*------------------------------------*/

  var CollectionSwitch = class extends HTMLElement {
    constructor() {
      super();
    }
  
    connectedCallback() {
      // Find the input checkbox with the class 'my-input'
      this.inputCheckbox = this.querySelector(".text-switch");
  
      // Listen for changes to the input checkbox
      this.inputCheckbox.addEventListener("change", this._onCheckboxChanged.bind(this));
    }
  
    _onCheckboxChanged(event) {
      let element = document.querySelector(".collection-blog");
      let tagHandle = this.getAttribute("tag-handle");
      let numberOfPosts;
  
      // If the checkbox is checked
      if (event.target.checked) {
        // Run the code that was originally in the button click handler
        _fetchBlogPosts(tagHandle, element, numberOfPosts);
  
        // Select all sections
        const sections = document.querySelectorAll('.shopify-section');
        
        // Initialize variables to track the start and end sections
        let hide = false;
  
        // Loop through each section
        sections.forEach(section => {
          if (section.classList.contains('shopify-section--collection-header')) {
            // Start hiding after this section
            hide = true;
          } else if (section.classList.contains('shopify-section--main-collection')) {
            // Stop hiding when this section is reached
            hide = false;
          } else if (hide) {
            // Toggle the hidden class for sections in between
            section.classList.toggle('hidden');
          }
        });
  
        // Hide the collection and show the collection-blog
        document.querySelector(".collection").style.display = "none";
        document.querySelector(".collection-blog").style.display = "block";
      } else {
        // If the checkbox is unchecked, reverse the changes
        document.querySelector(".collection").style.display = "grid";
        document.querySelector(".collection-blog").style.display = "none";
  
        // Reset hidden sections
        const sections = document.querySelectorAll('.shopify-section.hidden');
        sections.forEach(section => {
          section.classList.remove('hidden');
        });
      }
    }
  }
  
  if (!window.customElements.get("collection-switch")) {
    window.customElements.define("collection-switch", CollectionSwitch);
  }

  var SearchSwitch = class extends HTMLElement {
    constructor() {
      super();
    }
  
    connectedCallback() {
      // Find the input checkbox with the class 'my-input'
      this.inputCheckbox = this.querySelector(".text-switch");
  
      // Listen for changes to the input checkbox
      this.inputCheckbox.addEventListener("change", this._onCheckboxChanged.bind(this));
    }
  
    _onCheckboxChanged(event) {  
      // If the checkbox is checked
      if (event.target.checked) {
        // We search articles
        // Get search query
        this.dispatchEvent(new CustomEvent("search:switch", {
          bubbles: true,
          detail: {
            resource: "article"
          }
        }));
      } else {
        this.dispatchEvent(new CustomEvent("search:switch", {
          bubbles: true,
          detail: {
            resource: "product"
          }
        }));
      }
    }
  }
  
  if (!window.customElements.get("search-switch")) {
    window.customElements.define("search-switch", SearchSwitch);
  }

  /*------------------------------------*\
    #CUSTOMER ACCOUNT
  \*------------------------------------*/

  var AccountLogin = class extends HTMLElement {
    connectedCallback() {
      this.recoverForm = this.querySelector("#recover");
      this.loginForm = this.querySelector("#login");

      if (window.location.hash === "#recover") {
        this._switchForms();
      }

      new Delegate(this).on("click", '[href="#recover"], [href="#login"]', this._switchForms.bind(this));
    }

    _switchForms(event) {
      if (event) {
        event.preventDefault();
      }
      this.recoverForm.hidden = !this.recoverForm.hidden;
      this.loginForm.hidden = !this.loginForm.hidden;
    }
  };

  if (!window.customElements.get("account-login")) {
    window.customElements.define("account-login", AccountLogin);
  }



  var PrescriptionsBanner = class extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        sessionStorage.removeItem("pb:closed");
      }

      this.addEventListener("dialog:force-close", (event) => {
        this.hide();
        event.stopPropagation();
      });
    }

    connectedCallback() {
      this.elements = document.querySelectorAll("prescriptions-banner");
      this._checkStorage();
    }

    hide() {
      this.elements.forEach((element) => {
        element.classList.remove("show");
        element.classList.add("hide");
      });

      sessionStorage.setItem("pb:closed", "true");
    }

    show() {
      this.elements.forEach((element) => {
        element.classList.remove("hide");
        element.classList.add("show");
      });
    }

    _checkStorage() {
      if (sessionStorage.getItem("pb:closed") === "true") {
        return;
      } else {
        this.show();
      }
    }
  }

  if (!window.customElements.get("prescriptions-banner")) {
    window.customElements.define("prescriptions-banner", PrescriptionsBanner);
  }



  // Smooth scrolling for internal links

  (() => {
    var documentDelegate = new Delegate(document.body);

    documentDelegate.on('click', '[href^="#"], [data-href]', function (event, target) {
      var selector = target.hasAttribute('href') ? target.getAttribute('href') : target.getAttribute('data-href');

      if (selector === '#' || '#recover') {
        return;
      }

      var element = document.querySelector(selector);
      var offset = parseInt(target.getAttribute('data-offset') || 0);

      window.scrollTo({ 
        behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto", 
        top: element.offsetTop - offset 
      });

      event.preventDefault();
    });
  })();
  
})();
